/**
 * The Scripts step (D42, D43): a design's scripts and condition sets, a
 * structured editor for each, and a connectivity preview of the chosen step
 * drawn on the schematic.
 *
 * `useScripts` holds what the step shows; `ScriptsPanel` is its left side
 * (what there is, and a script's steps) and `ScriptsEditor` its right (the
 * step or condition set being edited). Drafts save by themselves shortly
 * after an edit, as the design does, and a stale save is reported, never
 * merged (A4).
 */
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, type Protocol } from "../api/client";
import type { DesignDocument, NodeDocument, TemplateDocument } from "../design/document";
import {
  AIR,
  COMPARISONS,
  SOURCE_KINDS,
  type Action,
  type Check,
  type Comparison,
  type ConditionSetDocument,
  type RunPump,
  type RunSetpoint,
  type RunSource,
  type SetSetpoint,
  type SetSource,
  type SetState,
  type ScriptDocument,
  type Source,
  type SourceKind,
  type Step,
  type StepRecord,
} from "../design/protocol";
import { initialPreview, isRigid, observesOf, parameterValue, previewScript, ratedSupply, sourcePorts, startAfter, type PreviewFinding, type StartPoint, type StepPreview } from "../scripts/preview";
import { nextState, portText, stateMatrix, type MatrixRow } from "../scripts/matrix";
import type { CommandContext } from "../editor/commands";
import { AddSelect, BareSelect, BareTextField, FieldRow, Fold, FoldRow, NumberField, Select, SheetRows, SheetTable, TextField } from "./fields";
import { useLiquidUnits } from "./DisplayUnitsContext";
import { TUBING_SET_UNITS, type LiquidUnits } from "./units";
import { operationText, profileShort, trackVolumes, profileText, profileValues, sourceValueText, stepTime, stepTimeText, withoutDeleted } from "../scripts/operations";
import { EndpointFields, FinishGlyphs, PumpFields, RegulatorProfileFields, regulatorProfile, SETPOINT_UNITS, SetpointFields, SourceProfileFields, sourceProfile } from "./StepOperations";
import { useKeepInside } from "../editor/keepInside";
import { RegulatorSettingEditor } from "../editor/RegulatorSettingEditor";
import { useStepTableWidths, WidthHandle } from "./StepTableWidths";
import { fluidColourOf } from "../render/SvgRenderer";
import { ProfileGlyph, PumpGlyph, SignalGlyph } from "./signals";
import { BagEditor } from "../editor/BagEditor";
import { PortSettingEditor } from "../editor/PortSettingEditor";
import type { MenuEntry } from "../editor/ContextMenu";
import type { Target } from "../editor/interaction";

type Finding = { code: string; message: string; severity: string; path?: string | null; detail?: Record<string, unknown> };

/** A finding from a screening: the steps it is about, and what on the drawing. */
export interface ScreenedFinding extends Finding {
  /** The steps it was met in, as `local_id`s; "" for step zero. */
  steps?: string[];
  /** The components and lines it is about, as the design names them. */
  refs?: string[];
}

/** Where a server finding is: its step (`path`, "initial" for step zero) and its element. */
export function locateScriptFinding(finding: Finding, script: ScriptDocument): ScreenedFinding {
  const stepIds = new Set((script.steps ?? []).map((step) => step.local_id));
  const steps = finding.path === "initial" ? [""] : finding.path && stepIds.has(finding.path) ? [finding.path] : [];
  const element = finding.detail?.element;
  return { ...finding, steps, refs: typeof element === "string" ? [element] : [] };
}

/**
 * The findings for one script, from a screening the author asked for. They
 * are a snapshot: editing the script, its conditions or the design marks
 * them out of date, and only screening again replaces them.
 */
export interface Screening {
  scriptId: string;
  at: string;
  findings: ScreenedFinding[];
  /** Why the server's checks could not run, when they could not. */
  problem: string | null;
  script: ScriptDocument;
  conditions: ConditionSetDocument | null;
  design: DesignDocument;
}

interface Loaded<T> {
  id: string;
  revision: number;
  content: T;
  saved: T;
}

/** A local identifier from free text: what the server accepts. */
export function toLocalId(text: string, fallback = "item"): string {
  const cleaned = text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const id = /^[a-z]/.test(cleaned) ? cleaned : `${fallback}_${cleaned}`.replace(/_+$/, "");
  return id.slice(0, 64) || fallback;
}

/** The next free `stem_N` among `used`. */
export function nextId(stem: string, used: Iterable<string>): string {
  const taken = new Set(used);
  let n = 1;
  while (taken.has(`${stem}_${n}`)) n++;
  return `${stem}_${n}`;
}

const SAVE_DELAY_MS = 800;
/** Edits closer together than this undo as one: a word typed, not each letter. */
const COALESCE_MS = 600;
const UNDO_LIMIT = 200;

/** The open script and condition set, as they were: a step of undo. */
interface Snapshot {
  script: ScriptDocument | null;
  conditions: ConditionSetDocument | null;
}

const NO_CONDITIONS: ConditionSetDocument = {};

/** What is being edited in place on the drawing: a port's status, or a bag's contents. */
export interface CanvasEdit {
  kind: "port" | "bag" | "pump" | "regulator";
  ref: string;
}

export interface ScriptsState {
  protocols: Protocol[] | null;
  scripts: Protocol[];
  conditionSets: Protocol[];
  script: Loaded<ScriptDocument> | null;
  conditions: Loaded<ConditionSetDocument> | null;
  /** The design's templates: a part's hold-up volume, a sensor's kind. */
  templates: Readonly<Record<string, TemplateDocument>>;
  /** -1: before the first step. */
  stepIndex: number;
  editing: "step" | "conditions" | null;
  previews: StepPreview[];
  preview: StepPreview | null;
  /** Step `at` (-1: step zero) as the drawing shows it: for an export of every step. */
  previewAt: (at: number) => StepPreview | null;
  /** The last screening of the open script, or null when it has not been screened. */
  screening: Screening | null;
  /** True when the script, its conditions or the design changed since the screening. */
  screeningStale: boolean;
  screeningBusy: boolean;
  /** Save what is open, then check it on the server and through every step of the preview. */
  screen: () => Promise<void>;
  findingsOpen: boolean;
  setFindingsOpen: (open: boolean) => void;
  problem: string | null;
  saving: boolean;
  openScript: (id: string) => void;
  openConditions: (id: string, edit?: boolean) => void;
  create: (kind: "script" | "condition_set", name: string) => Promise<void>;
  rename: (id: string, name: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  selectStep: (index: number) => void;
  editScript: (change: (script: ScriptDocument) => ScriptDocument) => void;
  /** Step back through the open script's and condition set's edits, or forward again. */
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  editConditions: (change: (conditions: ConditionSetDocument) => ConditionSetDocument) => void;
  editConditionsPanel: () => void;
  /**
   * Set a component's state, or a source's setting, where the author is: in
   * the chosen step (replacing what it set before), or, at step zero, as
   * where it starts.
   */
  setHere: (action: StateAction) => void;
  /** The same, in step `at` (-1: where it starts). */
  setAt: (at: number, action: StateAction) => void;
  /** What a pump does in the step shown, or (null) nothing: as the step before left it (D45). */
  setPump: (ref: string, operation: RunPump | null) => void;
  /**
   * A regulator's setpoint (pascal gauge; null for none) where the author
   * is: where it starts at step zero, else what the chosen step holds it at.
   */
  setRegulator: (ref: string, value: number | null) => void;
  /** A component to its next state, in step `at` (default: the chosen step). */
  toggleState: (ref: string, at?: number) => void;
  /** A source to its next setting, in step `at` (default: the chosen step). */
  cycleSource: (name: string, at?: number) => void;
  /**
   * A port component's source to `kind` at `magnitude` (pascal gauge, or
   * m³/s for flow) where the author is: its start at step zero, else what
   * the chosen step sets. The source and setting are made if need be.
   */
  setPortSetting: (ref: string, kind: SourceKind, magnitude: number | null) => void;
  /** Step zero, then what each step leaves: the table's columns. */
  columns: StepPreview[];
  /** Where the parent leaves things, when this script starts after one (D44). */
  startFrom: StartPoint | undefined;
  /** The script this one starts after (D44), if any. */
  parent: Protocol | null;
  /** Start after `parentId`, or (null) from the condition set again. */
  setStartsAfter: (parentId: string | null) => void;
  canvasEdit: CanvasEdit | null;
  setCanvasEdit: (edit: CanvasEdit | null) => void;
  /** What a bag or line holds at the start, and how much (m³). */
  setFill: (target: string, change: { fluid?: string; volume?: number | null; headspace?: number | null }) => void;
  /** A fluid by what it is called, made if need be: its name. */
  addFluid: (description: string, colour?: string) => string;
  setDesignation: (target: string, role: "" | "input" | "output") => void;
  /** A port's starting status (step zero): a source on it at that kind, made if need be. */
  setPortStart: (port: string, name: string, kind: SourceKind) => void;
  /** Make the condition set this script runs against, if there is none. */
  ensureConditions: () => Promise<void>;
}

/** A source setting of `kind` on `source`, added if it has none. */
export function withSetting(source: Source, kind: SourceKind): { source: Source; setting: string } {
  const found = source.settings.find((setting) => setting.kind === kind);
  if (found) return { source, setting: found.name };
  const names = source.settings.map((setting) => setting.name);
  const name = names.includes(kind) ? nextId(kind, names) : kind;
  return { source: { ...source, settings: [...source.settings, { name, kind }] }, setting: name };
}

/** A number as part of an identifier: 7.5 as "7p5". */
const idNumber = (value: number) => String(Number(value.toPrecision(4))).replace(".", "p").replace(/[^0-9p]/g, "");

/**
 * A setting of `kind` at `magnitude` on `source`, added if it has none. A
 * new magnitude is a new setting, never an edit of one: other steps may use
 * the old one. Vacuum is negative gauge, pressure positive, whichever sign
 * was typed.
 */
export function settingFor(
  source: Source,
  kind: SourceKind,
  magnitude: number | null,
): { source: Source; setting: string } {
  const gauged = kind === "pressure" || kind === "vacuum" || kind === "flow";
  const value =
    !gauged || magnitude === null ? null : kind === "vacuum" ? -Math.abs(magnitude) : kind === "pressure" ? Math.abs(magnitude) : magnitude;
  const found = source.settings.find(
    (setting) =>
      setting.kind === kind &&
      (value === null ? !gauged || setting.magnitude == null : setting.magnitude != null && Math.abs(setting.magnitude - value) < 1e-9 + Math.abs(value) * 1e-6),
  );
  if (found) return { source, setting: found.name };
  const stem =
    value === null
      ? kind === "open"
        ? "vent"
        : kind
      : kind === "flow"
        ? `flow_${idNumber(Math.abs(value) * 6e7)}_ml_min`
        : `${kind}_${idNumber(Math.abs(value) / 1000)}_kpa`;
  const names = source.settings.map((setting) => setting.name);
  const name = names.includes(stem) ? nextId(stem, names) : stem;
  return {
    source: { ...source, settings: [...source.settings, { name, kind, ...(value === null ? {} : { magnitude: value }) }] },
    setting: name,
  };
}

/**
 * Delete step `index` of the open script, once the author confirms, and show
 * the step before it (step zero if it was the first).
 */
export function deleteStep(s: ScriptsState, index: number): void {
  const step = s.script?.content.steps?.[index];
  if (!step) return;
  if (!window.confirm(`Delete step ${index + 1}, ${step.label}? Undo brings it back.`)) return;
  s.editScript((script) => ({ ...script, steps: (script.steps ?? []).filter((_, i) => i !== index) }));
  s.selectStep(index - 1);
}

/** A component's state or a source's setting: what a click on the drawing or a cell sets. */
export type StateAction = SetState | SetSource;

/** What an action is to: one per component, and per kind, in a step; one per source, a setting or a profile; one setpoint, held or profiled. */
export function actionKey(action: Action): string {
  if (action.kind === "set_source" || action.kind === "source_profile") return `source:${action.source}`;
  return action.kind === "setpoint_profile" ? `setpoint:${action.target}` : `${action.kind}:${action.target}`;
}

/** Replace what `actions` does to the same component or source, or add it. */
export function replaceAction(actions: readonly Action[], action: Action): Action[] {
  const key = actionKey(action);
  const same = (other: Action) => actionKey(other) === key;
  return actions.some(same) ? actions.map((other) => (same(other) ? action : other)) : [...actions, action];
}

export function useScripts(
  designId: string,
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  /** Load nothing until the Scripts step is first opened. */
  enabled = true,
  /** What pumps' volumes and rates read in beside them (D49). */
  units: LiquidUnits = TUBING_SET_UNITS,
): ScriptsState {
  const [protocols, setProtocols] = useState<Protocol[] | null>(null);
  const [script, setScript] = useState<Loaded<ScriptDocument> | null>(null);
  const [conditions, setConditions] = useState<Loaded<ConditionSetDocument> | null>(null);
  const [stepIndex, setStepIndex] = useState(-1);
  const [editing, setEditing] = useState<"step" | "conditions" | null>(null);
  const [screening, setScreening] = useState<Screening | null>(null);
  const [screeningBusy, setScreeningBusy] = useState(false);
  const [findingsOpen, setFindingsOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Every script's draft, the open one as it is being edited: for parents (D44). */
  const [drafts, setDrafts] = useState<Record<string, ScriptDocument>>({});
  const [canvasEdit, setCanvasEdit] = useState<CanvasEdit | null>(null);

  const reload = useCallback(async () => {
    try {
      setProtocols(await api.listProtocols(designId));
    } catch (caught) {
      setProblem(caught instanceof Error ? caught.message : "Could not load scripts");
    }
  }, [designId]);
  const [started, setStarted] = useState(enabled);
  useEffect(() => {
    if (enabled) setStarted(true);
  }, [enabled]);
  useEffect(() => {
    if (started) void reload();
  }, [reload, started]);

  const scripts = useMemo(() => (protocols ?? []).filter((p) => p.kind === "script"), [protocols]);
  const conditionSets = useMemo(() => (protocols ?? []).filter((p) => p.kind === "condition_set"), [protocols]);

  const load = async <T,>(id: string): Promise<Loaded<T>> => {
    const draft = await api.getProtocolDraft(id);
    const content = draft.content as unknown as T;
    return { id, revision: draft.revision, content, saved: content };
  };

  const openScript = useCallback((id: string) => {
    void load<ScriptDocument>(id)
      .then((loaded) => {
        setScript(loaded);
        setStepIndex(-1);
        setEditing(null);
        setCanvasEdit(null);
      })
      .catch((caught) => setProblem(caught instanceof Error ? caught.message : "Could not open the script"));
  }, []);
  const openConditions = useCallback((id: string, edit = false) => {
    void load<ConditionSetDocument>(id)
      .then((loaded) => {
        setConditions(loaded);
        if (edit) setEditing("conditions");
      })
      .catch((caught) => setProblem(caught instanceof Error ? caught.message : "Could not open the condition set"));
  }, []);

  // The first script, once listed: there is always something to look at.
  useEffect(() => {
    if (!script && scripts[0]) openScript(scripts[0].id);
  }, [scripts, script, openScript]);

  // Every script's draft, for the chain of parents; the open one as edited.
  useEffect(() => {
    const missing = scripts.filter((candidate) => !(candidate.id in drafts) && candidate.id !== script?.id);
    if (missing.length === 0) return;
    void Promise.all(missing.map((candidate) => api.getProtocolDraft(candidate.id).then((draft) => [candidate.id, draft.content] as const)))
      .then((loaded) => setDrafts((current) => ({ ...Object.fromEntries(loaded), ...current }) as Record<string, ScriptDocument>))
      .catch(() => undefined);
  }, [scripts, drafts, script?.id]);
  useEffect(() => {
    if (script) setDrafts((current) => (current[script.id] === script.content ? current : { ...current, [script.id]: script.content }));
  }, [script]);
  const draftsLoaded = scripts.every((candidate) => candidate.id in drafts);

  // The open script's condition set (D44): the one it names; else its
  // parent's; else, for a script from before D44, one no other script uses,
  // or one made for it.
  const openingConditions = useRef<string | null>(null);
  const linking = useRef<string | null>(null);
  useEffect(() => {
    if (!script) return;
    const named = script.content.conditions;
    const link = (id: string) =>
      setScript((current) => (current && current.id === script.id ? { ...current, content: { ...current.content, conditions: id } } : current));
    if (named) {
      if (conditions?.id !== named && openingConditions.current !== named) {
        openingConditions.current = named;
        openConditions(named);
      }
      return;
    }
    if (!draftsLoaded || protocols === null || linking.current === script.id) return;
    linking.current = script.id;
    const parent = script.content.starts_after ? drafts[script.content.starts_after] : undefined;
    if (parent?.conditions) return link(parent.conditions);
    const used = new Set(Object.entries(drafts).filter(([id]) => id !== script.id).map(([, draft]) => draft.conditions));
    const free = conditionSets.find((candidate) => !used.has(candidate.id));
    if (free) return link(free.id);
    const name = scripts.find((candidate) => candidate.id === script.id)?.name ?? "Script";
    void api
      .createProtocol(designId, "condition_set", `${name} conditions`)
      .then((made) => {
        link(made.id);
        return reload();
      })
      .catch((caught) => setProblem(caught instanceof Error ? caught.message : "Could not make its condition set"));
  }, [script, conditions?.id, draftsLoaded, drafts, protocols, conditionSets, scripts, designId, openConditions, reload]);

  // Findings belong to the script screened: another script, or none, and they go.
  useEffect(() => {
    if (screening && screening.scriptId !== script?.id) setScreening(null);
  }, [screening, script?.id]);

  // --- Saving -------------------------------------------------------------

  const save = useCallback(
    async <T,>(loaded: Loaded<T>, set: (next: (current: Loaded<T> | null) => Loaded<T> | null) => void) => {
      setSaving(true);
      try {
        const draft = await api.updateProtocolDraft(loaded.id, loaded.content as unknown as Record<string, unknown>, loaded.revision);
        set((current) =>
          current && current.id === loaded.id
            ? { ...current, revision: draft.revision, saved: loaded.content }
            : current,
        );
        setProblem(null);
        return true;
      } catch (caught) {
        if (caught instanceof ApiError && caught.isConflict) {
          setProblem("Someone else saved this first. Reopen it to see their changes, then reapply yours.");
        } else if (caught instanceof ApiError) {
          const detail = caught.findings.map((finding) => finding.message).join("; ");
          setProblem(detail ? `${caught.message}: ${detail}` : caught.message);
        } else {
          setProblem("Could not save");
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const scriptTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!script || script.content === script.saved) return;
    window.clearTimeout(scriptTimer.current);
    scriptTimer.current = window.setTimeout(() => {
      void save(script, setScript);
    }, SAVE_DELAY_MS);
    return () => window.clearTimeout(scriptTimer.current);
  }, [script, save]);

  const conditionsTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!conditions || conditions.content === conditions.saved) return;
    window.clearTimeout(conditionsTimer.current);
    conditionsTimer.current = window.setTimeout(() => {
      void save(conditions, setConditions);
    }, SAVE_DELAY_MS);
    return () => window.clearTimeout(conditionsTimer.current);
  }, [conditions, save]);

  // --- Undo ------------------------------------------------------------------

  // Every edit of the open script and its condition set, however it is made:
  // what they held before each. Edits in quick succession (typing) are one
  // step. Opening another script or condition set starts afresh.
  const history = useRef<{ key: string; past: Snapshot[]; future: Snapshot[]; last: Snapshot; at: number; applying: boolean }>({
    key: "",
    past: [],
    future: [],
    last: { script: null, conditions: null },
    at: 0,
    applying: false,
  });
  const [, setHistoryVersion] = useState(0);
  const historyKey = `${script?.id ?? ""}|${conditions?.id ?? ""}`;
  useEffect(() => {
    const h = history.current;
    const now: Snapshot = { script: script?.content ?? null, conditions: conditions?.content ?? null };
    if (h.key !== historyKey) {
      Object.assign(h, { key: historyKey, past: [], future: [], last: now, at: 0, applying: false });
      setHistoryVersion((version) => version + 1);
      return;
    }
    if (now.script === h.last.script && now.conditions === h.last.conditions) return;
    if (h.applying) {
      h.applying = false;
    } else {
      const time = Date.now();
      if (time - h.at > COALESCE_MS || h.past.length === 0) h.past = [...h.past, h.last].slice(-UNDO_LIMIT);
      h.future = [];
      h.at = time;
    }
    h.last = now;
    setHistoryVersion((version) => version + 1);
  }, [historyKey, script?.content, conditions?.content]);
  const restore = (snapshot: Snapshot) => {
    history.current.applying = true;
    history.current.at = 0;
    if (snapshot.script) setScript((current) => (current ? { ...current, content: snapshot.script! } : current));
    if (snapshot.conditions) setConditions((current) => (current ? { ...current, content: snapshot.conditions! } : current));
  };
  const undo = () => {
    const h = history.current;
    const snapshot = h.past[h.past.length - 1];
    if (!snapshot) return;
    h.past = h.past.slice(0, -1);
    h.future = [h.last, ...h.future];
    restore(snapshot);
  };
  const redo = () => {
    const h = history.current;
    const snapshot = h.future[0];
    if (!snapshot) return;
    h.future = h.future.slice(1);
    h.past = [...h.past, h.last];
    restore(snapshot);
  };

  // What the script asks of a component since deleted goes with it: nothing
  // could show or remove it. Not an edit to undo: undone, it would go again.
  useEffect(() => {
    if (!script) return;
    const kept = withoutDeleted(script.content, design);
    if (kept === script.content) return;
    history.current.applying = true;
    setScript({ ...script, content: kept });
  }, [script, design]);

  // --- The preview --------------------------------------------------------

  const conditionsDoc = conditions?.content ?? NO_CONDITIONS;
  // The scripts this one starts after, root first (D44). A cycle stops the chain.
  const parents = useMemo(() => {
    const chain: { id: string; content: ScriptDocument }[] = [];
    const seen = new Set(script ? [script.id] : []);
    let at = script?.content.starts_after ?? null;
    while (at && !seen.has(at) && drafts[at]) {
      seen.add(at);
      chain.unshift({ id: at, content: drafts[at]! });
      at = drafts[at]!.starts_after ?? null;
    }
    return chain;
  }, [script, drafts]);
  const from = useMemo<StartPoint | undefined>(
    () => (parents.length ? startAfter(design, templates, conditionsDoc, parents.map((parent) => parent.content)) : undefined),
    [design, templates, conditionsDoc, parents],
  );
  const previews = useMemo(
    () => (script ? previewScript(design, templates, conditionsDoc, script.content, from) : []),
    [design, templates, conditionsDoc, script, from],
  );
  const start = useMemo(
    () => initialPreview(design, templates, conditionsDoc, script?.content, from),
    [design, templates, conditionsDoc, script?.content, from],
  );
  // What each vessel holds after each step, net of hold-up (D60), as the table reads it.
  const tracked = useMemo(
    () => (script ? trackVolumes(design, conditionsDoc, script.content, [start, ...previews], templates) : []),
    [design, conditionsDoc, script, start, previews, templates],
  );
  /** Step `at` (-1: step zero) as the drawing shows it. */
  const previewAt = useCallback(
    (at: number): StepPreview | null => {
      const base = script ? (at < 0 ? start : (previews[at] ?? start)) : null;
      const step = at >= 0 ? script?.content.steps?.[at] : undefined;
      if (!base || !step) return base;
      // What each pump does in the step, drawn beside it (D45).
      const operations = Object.fromEntries(
        (step.actions ?? []).filter((action): action is RunPump => action.kind === "pump").map((action) => [action.target, operationText(action, units)]),
      );
      // And what each vessel holds after it, where known: on the drawing as in the table.
      const volumes = Object.fromEntries(
        Object.entries(tracked[at + 1] ?? {}).filter(
          (entry): entry is [string, number] => typeof entry[1] === "number" && (entry[1] > 0 || Boolean(base.nodes[entry[0]]?.liquid)),
        ),
      );
      return { ...base, operations, volumes };
    },
    [script, start, previews, units, tracked],
  );
  const preview = useMemo(() => previewAt(stepIndex), [previewAt, stepIndex]);

  const screen = async () => {
    if (!script) return;
    const screened = { script: script.content, conditions: conditions?.content ?? null, design };
    setScreeningBusy(true);
    try {
      // The server checks what is saved: what is on screen goes first.
      window.clearTimeout(scriptTimer.current);
      window.clearTimeout(conditionsTimer.current);
      if (script.content !== script.saved) await save(script, setScript);
      if (conditions && conditions.content !== conditions.saved) await save(conditions, setConditions);
      const findings: ScreenedFinding[] = [];
      let serverProblem: string | null = null;
      try {
        const checked = [...(await api.checkProtocol(script.id, conditions?.id)).findings];
        if (conditions) checked.push(...(await api.checkProtocol(conditions.id)).findings);
        findings.push(...checked.map((finding) => locateScriptFinding(finding as Finding, script.content)));
      } catch (caught) {
        serverProblem = caught instanceof Error ? caught.message : "Could not check the script";
      }
      // The preview's, once each however many steps meet it, with the steps that do.
      const steps = script.content.steps ?? [];
      const met = new Map<string, ScreenedFinding>();
      [start, ...previews].forEach((column, index) => {
        const stepId = index === 0 ? "" : (steps[index - 1]?.local_id ?? "");
        for (const finding of (column.findings ?? []) as readonly PreviewFinding[]) {
          const key = `${finding.code}:${finding.message}`;
          const seen = met.get(key);
          if (seen) {
            if (!seen.steps!.includes(stepId)) seen.steps!.push(stepId);
          } else met.set(key, { code: finding.code, message: finding.message, severity: "preview", steps: [stepId], refs: [...finding.refs] });
        }
      });
      findings.push(...met.values());
      setScreening({ scriptId: script.id, at: new Date().toISOString(), findings, problem: serverProblem, ...screened });
      setFindingsOpen(true);
    } finally {
      setScreeningBusy(false);
    }
  };

  return {
    protocols,
    scripts,
    conditionSets,
    script,
    conditions,
    templates,
    stepIndex,
    editing,
    previews,
    preview,
    previewAt,
    canUndo: history.current.past.length > 0,
    canRedo: history.current.future.length > 0,
    undo,
    redo,
    columns: script ? [start, ...previews] : [],
    startFrom: from,
    parent: script?.content.starts_after
      ? (scripts.find((candidate) => candidate.id === script.content.starts_after) ?? null)
      : null,
    canvasEdit,
    setCanvasEdit,
    setStartsAfter: (parentId) => {
      if (!script) return;
      const previous = script.content.conditions ?? null;
      const parentConditions = parentId ? (drafts[parentId]?.conditions ?? null) : null;
      if (parentId) {
        setScript((current) =>
          current ? { ...current, content: { ...current.content, starts_after: parentId, conditions: parentConditions ?? current.content.conditions } } : current,
        );
        // Its own condition set is no longer used, unless something else uses it.
        const shared = Object.entries(drafts).some(([id, draft]) => id !== script.id && draft.conditions === previous);
        if (previous && previous !== parentConditions && !shared) void api.deleteProtocol(previous).then(reload).catch(() => undefined);
        return;
      }
      // On its own again: a copy of the set-up it ran on, to change without changing the parent's.
      const name = scripts.find((candidate) => candidate.id === script.id)?.name ?? "Script";
      void api
        .createProtocol(designId, "condition_set", `${name} conditions`, conditionsDoc as Record<string, unknown>)
        .then((made) => {
          setScript((current) =>
            current ? { ...current, content: { ...current.content, starts_after: null, conditions: made.id, initial_sources: [] } } : current,
          );
          return reload();
        })
        .catch((caught) => setProblem(caught instanceof Error ? caught.message : "Could not copy the condition set"));
    },
    setFill: (target, change) =>
      setConditions((current) => {
        if (!current) return current;
        const fill = current.content.initial_fill ?? [];
        const existing = fill.find((entry) => entry.target === target);
        const merged = { target, fluid: existing?.fluid ?? AIR, volume: existing?.volume ?? null, headspace: existing?.headspace ?? null, ...change };
        // No headspace is the usual: left out rather than written as null.
        const { headspace, ...plain } = merged;
        const next = headspace ? { ...plain, headspace } : plain;
        const rest = fill.filter((entry) => entry.target !== target);
        // Air with no volume is what an unlisted place holds: not worth a row.
        const keep = next.fluid !== AIR || (next.volume ?? null) !== null;
        return { ...current, content: { ...current.content, initial_fill: keep ? [...rest, next] : rest } };
      }),
    addFluid: (description, colour) => {
      const fluids = conditions?.content.fluids ?? [];
      const found = fluids.find((fluid) => (fluid.description || fluid.name).toLowerCase() === description.trim().toLowerCase());
      if (found) return found.name;
      const stem = toLocalId(description, "fluid");
      const names = fluids.map((fluid) => fluid.name);
      const name = stem === AIR || names.includes(stem) ? nextId(stem, names) : stem;
      setConditions((current) =>
        current
          ? {
              ...current,
              content: { ...current.content, fluids: [...(current.content.fluids ?? []), { name, description: description.trim(), ...(colour ? { colour } : {}) }] },
            }
          : current,
      );
      return name;
    },
    setDesignation: (target, role) =>
      setConditions((current) =>
        current
          ? {
              ...current,
              content: {
                ...current.content,
                designations: [...(current.content.designations ?? []).filter((entry) => entry.target !== target), ...(role ? [{ target, role }] : [])],
              },
            }
          : current,
      ),
    screening,
    screeningStale:
      screening !== null &&
      (screening.script !== script?.content || screening.conditions !== (conditions?.content ?? null) || screening.design !== design),
    screeningBusy,
    screen,
    findingsOpen,
    setFindingsOpen,
    problem,
    saving,
    openScript,
    openConditions,
    create: async (kind, name) => {
      try {
        const made = await api.createProtocol(designId, kind, name);
        await reload();
        if (kind === "script") openScript(made.id);
        else openConditions(made.id, true);
      } catch (caught) {
        setProblem(caught instanceof Error ? caught.message : "Could not create it");
      }
    },
    rename: async (id, name) => {
      try {
        await api.renameProtocol(id, name);
        await reload();
      } catch (caught) {
        setProblem(caught instanceof Error ? caught.message : "Could not rename it");
      }
    },
    remove: async (id) => {
      try {
        await api.deleteProtocol(id);
        // Off the list at once: else the first script listed, this one, is opened again.
        setProtocols((current) => current?.filter((candidate) => candidate.id !== id) ?? current);
        setProblem(null);
        // A script's condition set goes with it, unless another uses it.
        if (script?.id === id) {
          setScript(null);
          setConditions(null);
          setCanvasEdit(null);
        }
        if (conditions?.id === id) setConditions(null);
        setDrafts((current) => Object.fromEntries(Object.entries(current).filter(([other]) => other !== id)));
        await reload();
      } catch (caught) {
        setProblem(caught instanceof Error ? caught.message : "Could not delete it");
      }
    },
    selectStep: (index) => {
      setStepIndex(index);
      setEditing(index >= 0 ? "step" : null);
    },
    editScript: (change) => setScript((current) => (current ? { ...current, content: change(current.content) } : current)),
    editConditions: (change) =>
      setConditions((current) => (current ? { ...current, content: change(current.content) } : current)),
    editConditionsPanel: () => setEditing("conditions"),
    setHere: (action) => setAt(stepIndex, action),
    setPump: (ref, operation) => {
      if (stepIndex < 0) return;
      setScript((current) =>
        current
          ? {
              ...current,
              content: {
                ...current.content,
                steps: (current.content.steps ?? []).map((step, index) => {
                  if (index !== stepIndex) return step;
                  const rest = (step.actions ?? []).filter((action) => !(action.kind === "pump" && action.target === ref));
                  return { ...step, actions: operation ? [...rest, operation] : rest };
                }),
              },
            }
          : current,
      );
    },
    setRegulator: (ref, value) => {
      const want = value ?? 0;
      if (stepIndex < 0) {
        // The script's own start, over where the parent left it.
        const same = want === (from?.regulated?.[ref] ?? 0);
        setScript((current) =>
          current
            ? {
                ...current,
                content: {
                  ...current.content,
                  initial_setpoints: [
                    ...(current.content.initial_setpoints ?? []).filter((start) => start.target !== ref),
                    ...(same ? [] : [{ target: ref, value: want }]),
                  ],
                },
              }
            : current,
        );
        return;
      }
      // Held where the step before left it, the step need say nothing.
      const before = columnAt(stepIndex - 1)?.regulators?.[ref];
      const same = !before?.sweep && want === (before?.asked ?? 0);
      const action: SetSetpoint = { kind: "setpoint", target: ref, quantity: "pressure", value: want };
      setScript((current) =>
        current
          ? {
              ...current,
              content: {
                ...current.content,
                steps: (current.content.steps ?? []).map((step, index) => {
                  if (index !== stepIndex) return step;
                  const rest = (step.actions ?? []).filter((other) => actionKey(other) !== actionKey(action));
                  return { ...step, actions: same ? rest : [...rest, action] };
                }),
              },
            }
          : current,
      );
    },
    setAt: (at, action) => setAt(at, action),
    toggleState: (ref, at = stepIndex) => {
      const node = design.nodes?.find((candidate) => candidate.local_id === ref);
      // In a step, a pump's operation has more to it than on or off: its editor opens (D45).
      if (node?.role === "pump" && at >= 0) {
        setCanvasEdit({ kind: "pump", ref });
        return;
      }
      if (!node?.state_set?.length) return;
      const column = columnAt(at);
      const now = column?.states[ref] ?? node.initial_state ?? node.state_set[0];
      setAt(at, { kind: "set_state", target: ref, state: nextState(node.state_set, now) });
    },
    cycleSource: (name, at = stepIndex) => {
      const source = conditions?.content.sources?.find((candidate) => candidate.name === name);
      if (!source || source.settings.length === 0) return;
      const now = columnAt(at)?.sources[name];
      const names = source.settings.map((setting) => setting.name);
      setAt(at, { kind: "set_source", source: name, setting: nextState(names, now) });
    },
    setPortSetting: (ref, kind, magnitude) => {
      if (!conditions) {
        setProblem("Create a condition set first: a port's status belongs to it.");
        return;
      }
      const node = design.nodes?.find((candidate) => candidate.local_id === ref);
      const sources = conditions.content.sources ?? [];
      const existing = sources.find((source) => source.port.split(".")[0] === ref);
      const port =
        existing?.port ??
        (node ? sourcePorts(node)[0] : undefined);
      if (!port) return;
      const taken = new Set(sources.filter((source) => source !== existing).map((source) => source.name));
      let base: Source = existing ?? { name: toLocalId(node?.label || ref, "source"), port, settings: [], initial: "" };
      if (!existing && taken.has(base.name)) base = { ...base, name: nextId(base.name, taken) };
      if (!existing && (stepIndex >= 0 || from)) {
        // New in a later step: until then it was as an unsourced port is, open at its end (D59).
        // A drawn supply was live at its rating until now (D66).
        const rated = node ? ratedSupply(node, templates) : null;
        const before = rated
          ? settingFor(base, rated.kind, rated.magnitude)
          : settingFor(base, node?.role === "port" || node?.role === "filter" ? "open" : "sealed", null);
        base = { ...before.source, initial: before.setting };
      }
      const { source, setting } = settingFor(base, kind, magnitude);
      // At step zero the setting is where the source starts; in a step, the step sets it.
      // At step zero after a parent, the script sets it (initial_sources); the parent's world stays as it is.
      const next = stepIndex < 0 && !from ? { ...source, initial: setting } : source;
      if (next !== existing) {
        setConditions((current) =>
          current
            ? {
                ...current,
                content: {
                  ...current.content,
                  sources: existing
                    ? (current.content.sources ?? []).map((candidate) => (candidate.name === existing.name ? next : candidate))
                    : [...(current.content.sources ?? []), next],
                },
              }
            : current,
        );
      }
      if (stepIndex >= 0 || from) setAt(stepIndex, { kind: "set_source", source: next.name, setting });
    },
    setPortStart: (port, name, kind) =>
      setConditions((current) => {
        if (!current) return current;
        const sources = current.content.sources ?? [];
        const existing = sources.find((source) => source.port === port);
        const base: Source = existing ?? {
          name: toLocalId(name, "source"),
          port,
          settings: [],
          initial: "",
        };
        // A name no other source has.
        const taken = new Set(sources.filter((source) => source !== existing).map((source) => source.name));
        if (taken.has(base.name)) base.name = nextId(base.name, taken);
        const { source, setting } = withSetting(base, kind);
        const next = { ...source, initial: setting };
        return {
          ...current,
          content: {
            ...current.content,
            sources: existing ? sources.map((candidate) => (candidate === existing ? next : candidate)) : [...sources, next],
          },
        };
      }),
    ensureConditions: async () => {
      if (conditions) return;
      try {
        const made = await api.createProtocol(designId, "condition_set", "Initial conditions");
        await reload();
        openConditions(made.id);
      } catch (caught) {
        setProblem(caught instanceof Error ? caught.message : "Could not create the condition set");
      }
    },
  };

  /** What the table's column for `at` shows: step zero for -1, else what that step leaves. */
  function columnAt(at: number): StepPreview | undefined {
    return at < 0 ? start : previews[at];
  }

  /**
   * Set a component or source in step `at` (-1: where it starts). An action
   * that only repeats what the step before left is dropped, so cycling a cell
   * back leaves the script as it was.
   */
  function setAt(at: number, action: StateAction) {
    if (at >= 0) {
      const before = at === 0 ? start : previews[at - 1];
      const same =
        action.kind === "set_state" ? before?.states[action.target] === action.state : before?.sources[action.source] === action.setting;
      const sameTarget = (other: Action) => actionKey(other) === actionKey(action);
      setScript((current) =>
        current
          ? {
              ...current,
              content: {
                ...current.content,
                steps: (current.content.steps ?? []).map((step, index) =>
                  index !== at
                    ? step
                    : {
                        ...step,
                        actions: same
                          ? (step.actions ?? []).filter((other) => !sameTarget(other))
                          : replaceAction(step.actions ?? [], action),
                      },
                ),
              },
            }
          : current,
      );
      return;
    }
    if (action.kind === "set_source" && from) {
      // After a parent, step zero's settings are the script's own, over where the parent left them.
      const same = from.sources[action.source] === action.setting;
      setScript((current) =>
        current
          ? {
              ...current,
              content: {
                ...current.content,
                initial_sources: [
                  ...(current.content.initial_sources ?? []).filter((start) => start.source !== action.source),
                  ...(same ? [] : [action]),
                ],
              },
            }
          : current,
      );
      return;
    }
    if (action.kind === "set_state") {
      const declared = from
        ? from.states[action.target]
        : design.nodes?.find((node) => node.local_id === action.target)?.initial_state;
      setScript((current) =>
        current
          ? {
              ...current,
              content: {
                ...current.content,
                initial_states: [
                  ...(current.content.initial_states ?? []).filter((start) => start.target !== action.target),
                  ...(declared === action.state ? [] : [action]),
                ],
              },
            }
          : current,
      );
    } else {
        setConditions((current) =>
          current
            ? {
                ...current,
                content: {
                  ...current.content,
                  sources: (current.content.sources ?? []).map((source) =>
                    source.name === action.source ? { ...source, initial: action.setting } : source,
                  ),
                },
              }
            : current,
        );
    }
  }
}

// --- Labels for what the design holds ----------------------------------------

const labelOf = (node: NodeDocument) => (node.label ? `${node.label} (${node.local_id})` : node.local_id);

/** A sensor's reading as a person types it: pressure in kPa, flow in the design's unit, a detector as yes or no. */
export function readingUnit(observes: string | undefined, units: LiquidUnits = TUBING_SET_UNITS): { label: string; factor: number } {
  switch (observes) {
    case "pressure":
      return { label: "kPa", factor: 1000 };
    case "flow_rate":
      return { label: units.flowRate.label, factor: units.flowRate.factor };
    case "air_in_line":
      return { label: "(1 detects, 0 does not)", factor: 1 };
    case "temperature":
      return { label: "K", factor: 1 };
    default:
      return { label: "", factor: 1 };
  }
}

// --- The left side ------------------------------------------------------------

/** Which script, or which condition set: a list to choose from, and what can be done to the one chosen. */
function ProtocolPicker({
  title,
  kind,
  items,
  current,
  onOpen,
  onCreate,
  onRename,
  onRemove,
  onEdit,
}: {
  title: string;
  kind: string;
  items: Protocol[];
  current: string | undefined;
  onOpen: (id: string) => void;
  onCreate: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onEdit?: () => void;
}) {
  const id = useId();
  const chosen = items.find((item) => item.id === current);
  return (
    <div className="sx-picker">
      <label htmlFor={id}>{title}</label>
      <select id={id} value={current ?? ""} onChange={(event) => event.target.value && onOpen(event.target.value)}>
        {!chosen && <option value="">{items.length ? "Choose…" : "None"}</option>}
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
            {item.latest_version_number ? ` v${item.latest_version_number}` : ""}
          </option>
        ))}
      </select>
      {onEdit && chosen && (
        <button type="button" className="sx-icon" aria-label={`Edit ${chosen.name}`} title="Edit" onClick={onEdit}>
          ≡
        </button>
      )}
      <button
        type="button"
        className="sx-icon"
        aria-label={`New ${kind}`}
        title={`New ${kind}`}
        onClick={() => {
          const name = window.prompt(`New ${kind}`);
          if (name && name.trim()) onCreate(name.trim());
        }}
      >
        +
      </button>
      {chosen && (
        <button
          type="button"
          className="sx-icon"
          aria-label={`Rename ${chosen.name}`}
          title="Rename"
          onClick={() => {
            const next = window.prompt(`Rename ${chosen.name}`, chosen.name);
            if (next && next.trim() && next !== chosen.name) onRename(chosen.id, next.trim());
          }}
        >
          ✎
        </button>
      )}
      {chosen && !chosen.latest_version_number && (
        <button
          type="button"
          className="sx-icon"
          aria-label={`Delete ${chosen.name}`}
          title="Delete (never published)"
          onClick={() => window.confirm(`Delete ${chosen.name}? It has never been published.`) && onRemove(chosen.id)}
        >
          ×
        </button>
      )}
    </div>
  );
}


const ROW_GROUPS: readonly { kind: MatrixRow["kind"]; title: string }[] = [
  // Each step's time, where its finish bounds it: first, with no heading.
  { kind: "time", title: "" },
  { kind: "state", title: "States" },
  { kind: "setpoint", title: "Regulators" },
  { kind: "reading", title: "Instruments" },
  { kind: "source", title: "Ports" },
  { kind: "bag", title: "Bags and chambers" },
];

/**
 * The script as a table: a row for each valve, pump, connector, port and
 * bag. Compact, it has two columns, the step before and the step shown, so
 * it never grows with the script; expanded, over the whole canvas, a column
 * for every step from zero. A cell reads what that step leaves, bold where
 * the step changed it. Clicking a state cycles it, a port or bag opens its
 * editor on the drawing.
 */
/** The least room (px) the names keep in the compact table. */
const NAMES_ROOM = 108;

function StepMatrix({
  scripts: s,
  design,
  expanded,
  onAdd,
  onMove,
}: {
  scripts: ScriptsState;
  design: DesignDocument;
  expanded: boolean;
  /** A new step after the one shown. */
  onAdd: () => void;
  /** The step shown, one place earlier (-1) or later (1). */
  onMove: (by: -1 | 1) => void;
}) {
  const steps = s.script?.content.steps ?? [];
  const units = useLiquidUnits();
  // Room for a third step beside the names: the pane's width decides.
  const wrap = useRef<HTMLDivElement>(null);
  const [steps3, setSteps3] = useState(false);
  // One width for every step, and the names' width where the table is expanded.
  const sizing = useStepTableWidths();
  const stepWidth = sizing.widths.step;
  const rows = useMemo(
    () => stateMatrix(design, s.conditions?.content ?? {}, s.columns, s.script?.content ?? null, units, s.templates),
    [design, s.conditions?.content, s.columns, s.script?.content, units, s.templates],
  );
  const title = (column: number) =>
    column === 0 ? (s.parent ? `After ${s.parent.name}` : "Initial conditions") : (steps[column - 1]?.label ?? `Step ${column}`);
  const click = (row: MatrixRow, column: number) => {
    const at = column - 1;
    s.selectStep(at);
    if (row.kind === "state") s.toggleState(row.id, at);
    // A port's status, a regulator's setpoint and a bag's contents are edited on the drawing, where they read.
    else s.setCanvasEdit({ kind: row.kind === "bag" ? "bag" : row.kind === "setpoint" ? "regulator" : "port", ref: row.id });
  };
  // A bag's contents are set at step zero, and only by a script that does not start after another.
  // A regulator a step sweeps is edited in the step's panel, where the sweep is.
  const settable = (row: MatrixRow, column: number) =>
    row.kind !== "time" &&
    row.kind !== "reading" &&
    !row.inline &&
    !row.chamber &&
    (row.kind !== "bag" || (column === 0 && !s.parent)) &&
    (row.kind !== "setpoint" || !s.columns[column]?.regulators?.[row.id]?.sweep);
  const hasRows = rows.length > 0;
  useEffect(() => {
    const element = wrap.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const fit = () => setSteps3(element.clientWidth >= NAMES_ROOM + 3 * stepWidth);
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasRows, stepWidth]);
  if (!hasRows) return <p className="ws-muted ws-small">Nothing in this design has a state, a source or a bag.</p>;
  const current = s.stepIndex + 1;
  // Compact: the step before (none before step zero) and the step shown, and
  // where the pane has room the step after: the window moves at either end so
  // the step shown is always in it.
  const last = s.columns.length - 1;
  const three = steps3 && last >= 2;
  const from = three ? Math.max(0, Math.min(current - 1, last - 2)) : current - 1;
  const shown = expanded ? s.columns.map((_, column) => column) : Array.from({ length: three ? 3 : 2 }, (_, index) => from + index);
  const blank = (column: number) => column < 0;
  return (
    <div ref={wrap} className={`sx-matrix-wrap${expanded ? " sx-matrix-wrap-expanded" : ""}`}>
      {!expanded && (
        <div className="sx-matrix-nav" role="group" aria-label="Table step">
          <button type="button" aria-label="Table: previous step" disabled={current <= 0} onClick={() => s.selectStep(current - 2)}>
            ◀
          </button>
          <span className="sx-matrix-nav-step" title={title(current)}>
            {current}/{s.columns.length - 1} · {title(current)}
          </span>
          <button type="button" aria-label="Table: next step" disabled={current >= s.columns.length - 1} onClick={() => s.selectStep(current)}>
            ▶
          </button>
          <span className="sx-matrix-nav-tools">
            <button type="button" aria-label="Move this step earlier" title="Move earlier" disabled={current <= 1} onClick={() => onMove(-1)}>
              ↑
            </button>
            <button
              type="button"
              aria-label="Move this step later"
              title="Move later"
              disabled={current <= 0 || current >= s.columns.length - 1}
              onClick={() => onMove(1)}
            >
              ↓
            </button>
            <button type="button" aria-label="Add a step after this one" title="Add a step after this one" onClick={onAdd}>
              +
            </button>
          </span>
        </div>
      )}
      <table
        className={`sx-matrix${expanded ? " sx-matrix-expanded" : " sx-matrix-compact"}`}
        aria-label="States by step"
        // Expanded, the columns are exactly as wide as set: the pane scrolls.
        // Compact, the names keep some room: a narrow pane scrolls rather than crushing them.
        style={
          expanded
            ? { width: sizing.widths.name + shown.length * stepWidth }
            : { minWidth: NAMES_ROOM + shown.length * stepWidth }
        }
      >
        {/* Fixed widths: the table keeps its shape whatever the steps hold. Compact, the names take what the steps leave. */}
        <colgroup>
          <col className="sx-col-name" style={expanded ? { width: sizing.widths.name } : undefined} />
          {shown.map((column) => (
            <col key={column} className="sx-col-step" style={{ width: stepWidth }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th scope="col" className="sx-matrix-corner">
              {expanded ? "Step" : ""}
              {expanded && <WidthHandle sizing={sizing} column="name" label="Resize the names column" />}
            </th>
            {shown.map((column) =>
              blank(column) ? (
                <th key="none" scope="col" className="sx-matrix-none">
                  <span title="Nothing before step zero">—</span>
                  <WidthHandle sizing={sizing} column="step" label="Resize the step columns" />
                </th>
              ) : (
              <th key={column} scope="col" aria-current={column - 1 === s.stepIndex || undefined} title={title(column)}>
                <div className="sx-matrix-head">
                  <button type="button" className="sx-matrix-step" aria-label={`Step ${column}: ${title(column)}`} onClick={() => s.selectStep(column - 1)}>
                    {column}
                    {column > 0 && flagged(s, steps[column - 1]?.local_id) && <span className="sx-flag-dot" aria-hidden="true" />}
                  </button>
                  {/* On the step's own column, so which step goes is plain. Step zero is the start: never deleted. */}
                  {column > 0 && (
                    <button
                      type="button"
                      className="sx-matrix-delete"
                      aria-label={`Delete step ${column}: ${title(column)}`}
                      title={`Delete step ${column}`}
                      onClick={() => deleteStep(s, column - 1)}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
                <WidthHandle sizing={sizing} column="step" label="Resize the step columns" />
              </th>
              ),
            )}
          </tr>
        </thead>
        {ROW_GROUPS.map((group) => {
          const members = rows.filter((row) => row.kind === group.kind);
          if (members.length === 0) return null;
          return (
            <tbody key={group.kind}>
              {group.title && (
                <tr className="sx-matrix-group">
                  <th scope="rowgroup" colSpan={shown.length + 1}>
                    {group.title}
                  </th>
                </tr>
              )}
              {members.map((row) => (
                <tr key={`${row.kind}-${row.id}`}>
                  <th scope="row" title={row.label}>
                    {row.label}
                  </th>
                  {shown.map((column) => {
                    const cell = row.cells[column];
                    if (!cell)
                      return (
                        <td key="none" className="sx-matrix-none">
                          <span className="sx-matrix-value">—</span>
                        </td>
                      );
                    const label = `${row.label}, step ${column}: ${cell.text}${cell.sub ? ` ${cell.sub}` : ""}`;
                    // The hover says what the cell says, in full where the cell cuts it short.
                    const shownText = cell.sub ? `${cell.text} ${cell.sub}` : cell.text;
                    const content = (
                      <>
                        {cell.operation && <PumpGlyph pump={cell.operation} />}
                        {cell.glyph ? (
                          <>
                            <SignalGlyph shape={cell.glyph} />
                            {cell.short}
                          </>
                        ) : (
                          cell.text
                        )}
                        {cell.sub && <span className="sx-matrix-sub">{cell.sub}</span>}
                      </>
                    );
                    return (
                      <td key={column} data-tone={cell.tone} data-certainty={cell.certainty} data-changed={cell.changed || undefined} aria-current={column - 1 === s.stepIndex || undefined}>
                        {settable(row, column) ? (
                          <button type="button" aria-label={`${label}. Change it`} title={shownText} onClick={() => click(row, column)}>
                            {content}
                          </button>
                        ) : (
                          <span
                            className="sx-matrix-value"
                            title={shownText}
                          >
                            {content}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

export function ScriptsPanel({ scripts: s, design }: { scripts: ScriptsState; design: DesignDocument }) {
  const steps = s.script?.content.steps ?? [];
  // Over the whole canvas: every step at once.
  const [expanded, setExpanded] = useState(false);
  const move = (from: number, to: number) =>
    s.editScript((script) => {
      const list = [...(script.steps ?? [])];
      const [item] = list.splice(from, 1);
      list.splice(to, 0, item!);
      return { ...script, steps: list };
    });
  // After the step shown (at step zero, first), and shown.
  const addStep = () => {
    const used = steps.flatMap((step) => [
      step.local_id,
      ...(step.checks ?? []).map((check) => check.local_id),
      ...(step.records ?? []).map((record) => record.local_id),
    ]);
    const at = s.stepIndex + 1;
    const step: Step = {
      local_id: nextId("step", used),
      label: `Step ${steps.length + 1}`,
      actions: [],
      finish: { max_seconds: 5, on_max: "continue" },
    };
    s.editScript((script) => {
      const list = [...(script.steps ?? [])];
      list.splice(at, 0, step);
      return { ...script, steps: list };
    });
    s.selectStep(at);
  };
  return (
    <aside
      className={`ws-palette sx-panel${expanded ? " sx-panel-expanded" : ""}`}
      aria-label="Workflows"
      onKeyDown={(event) => {
        if (expanded && event.key === "Escape") setExpanded(false);
      }}
    >
      <div className="sx-scroll">
        {s.problem && (
          <p className="sx-problem" role="alert">
            {s.problem}
          </p>
        )}
        <div className="sx-pickers">
          <ProtocolPicker
            title="Script"
            kind="script"
            items={s.scripts}
            current={s.script?.id}
            onOpen={s.openScript}
            onCreate={(name) => void s.create("script", name)}
            onRename={(id, name) => void s.rename(id, name)}
            onRemove={(id) => void s.remove(id)}
          />
        </div>
        {s.script && (
          <>
            <section className="sx-section" aria-label="Table">
              <div className="sx-section-head">
                <h3>Steps</h3>
                <span className="ws-muted ws-small">{s.saving ? "Saving…" : s.script.content === s.script.saved ? "Saved" : "Unsaved"}</span>
                <button type="button" className="sx-small-button" aria-pressed={expanded} onClick={() => setExpanded((open) => !open)}>
                  {expanded ? "Collapse" : "Every step"}
                </button>
              </div>
              <StepMatrix
                scripts={s}
                design={design}
                expanded={expanded}
                onAdd={addStep}
                onMove={(by) => {
                  const at = s.stepIndex;
                  if (at < 0 || at + by < 0 || at + by >= steps.length) return;
                  move(at, at + by);
                  s.selectStep(at + by);
                }}
              />
            </section>
          </>
        )}
      </div>
    </aside>
  );
}

/**
 * What a record should read, where the preview can say (D53): for a source
 * whose profile reaches a wetted filter's bubble point, that level; for a
 * profile otherwise, its span; for a setting, its value. A sensor's reading
 * is the solver's, or the instrument's, to know.
 */
export function expectedRecord(
  record: StepRecord,
  step: Step,
  preview: StepPreview | undefined,
  units: LiquidUnits,
  names: Readonly<Record<string, string>> = {},
): string | null {
  if (!record.source) {
    // A sensor's peak in a step with a profile: where gas breaks through, or the profile's top (D56).
    const profile = (step.actions ?? []).find((action): action is RunSource => action.kind === "source_profile");
    if (!record.measure || record.measure === "value" || !profile || profile.quantity === "flow") return null;
    const reached = preview?.findings.find((finding) => finding.code === "bubble_point" && finding.value !== undefined);
    if (reached?.value !== undefined)
      return `about ${sourceValueText(reached.value, profile.quantity, units)} or more, where the profile reaches ${names[reached.refs[0] ?? ""] ?? reached.refs[0] ?? "the filter"}'s bubble point`;
    const top = profileValues(profile).at(-1);
    return top === undefined ? null : `up to ${sourceValueText(top, profile.quantity, units)}, where its profile peaks`;
  }
  const profile = (step.actions ?? []).find((action): action is RunSource => action.kind === "source_profile" && action.source === record.source);
  if (profile) {
    const reached = preview?.findings.find((finding) => finding.code === "bubble_point" && finding.source === record.source);
    if (reached?.value !== undefined)
      return `about ${sourceValueText(reached.value, profile.quantity, units)}, where it reaches ${names[reached.refs[0] ?? ""] ?? reached.refs[0] ?? "the filter"}'s bubble point`;
    const values = profileValues(profile);
    if (values.length === 0) return null;
    const [low, high] = [values[0]!, values.at(-1)!];
    return record.when === "met"
      ? `between ${sourceValueText(low, profile.quantity, units)} and ${sourceValueText(high, profile.quantity, units)}`
      : `${sourceValueText(high, profile.quantity, units)}, or where its finish stopped it`;
  }
  const port = Object.values(preview?.ports ?? {}).find((candidate) => candidate.source === record.source);
  return port ? portText(port, units) : null;
}

const SPEC_GROUPS = ["In this test", "Reached by this step's drive", "Elsewhere in the design"] as const;

/**
 * The components' parameters a check's spec can be (D55), in unit `si`, told
 * apart: first a filter whose bubble point the step's profile reaches, then
 * what the step's drive reaches, then the rest; each by its label and
 * identifier, with its value as it stands.
 */
export function specChoices(
  nodes: readonly NodeDocument[],
  templates: Readonly<Record<string, TemplateDocument>>,
  preview: StepPreview | undefined,
  si: string | null,
  unit: { label: string; factor: number },
): { value: string; label: string; group: string }[] {
  if (si === null) return [];
  const reachedBubble = new Set((preview?.findings ?? []).filter((finding) => finding.code === "bubble_point").flatMap((finding) => finding.refs));
  const driven = (id: string) => ["pressure", "vacuum", "flow"].includes(preview?.nodes[id]?.gas ?? "");
  return nodes
    .flatMap((node) => {
      const template = node.template_version_id ? templates[node.template_version_id] : undefined;
      return (template?.parameters ?? [])
        .filter((parameter) => (parameter.value_type === "float" || parameter.value_type === "int") && parameter.unit === si)
        .map((parameter) => {
          const now = parameterValue(node, templates, parameter.key);
          const reached = reachedBubble.has(node.local_id) && parameter.key === "bubble_point";
          const rank = reached ? 0 : driven(node.local_id) ? 1 : 2;
          const value = now === null ? "" : ` · ${Number((now / unit.factor).toPrecision(4))} ${unit.label}`;
          return {
            value: `${node.local_id}.${parameter.key}`,
            label: `${node.label || node.local_id} (${node.local_id}) · ${parameter.key.replace(/_/g, " ")}${value}${reached ? " · the profile reaches it" : ""}`,
            group: SPEC_GROUPS[rank]!,
            rank,
          };
        });
    })
    .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label))
    .map(({ value, label, group }) => ({ value, label, group }));
}

/**
 * The sensor observing `observes` nearest a port, through lines and whatever
 * the components join, whatever their state: the gauge on a source's line.
 */
export function nearestSensor(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  port: string,
  observes: string,
): string | null {
  const nodes = new Map((design.nodes ?? []).map((node) => [node.local_id, node]));
  const next = new Map<string, string[]>();
  for (const edge of design.edges ?? []) {
    const [a, b] = [edge.from_port.split(".")[0]!, edge.to_port.split(".")[0]!];
    next.set(a, [...(next.get(a) ?? []), b]);
    next.set(b, [...(next.get(b) ?? []), a]);
  }
  const start = port.split(".")[0]!;
  const seen = new Set([start]);
  let frontier = [start];
  while (frontier.length > 0) {
    const found = frontier
      .map((id) => nodes.get(id))
      .filter((node): node is NodeDocument => node?.role === "sensor" && observesOf(node, templates) === observes)
      .map((node) => node.local_id)
      .sort();
    if (found.length > 0) return found[0]!;
    frontier = frontier.flatMap((id) => (next.get(id) ?? []).filter((other) => !seen.has(other) && (seen.add(other), true)));
  }
  return null;
}

/** A setpoint in a few characters: "60 rpm", "37 °C", "200 kPa". */
export function setpointText(setpoint: SetSetpoint): string {
  const unit = SETPOINT_UNITS[setpoint.quantity];
  return `${Number(unit.fromSI(setpoint.value).toPrecision(4))} ${unit.label}`;
}

/** What a source's record measures: its profile's quantity in the step, else its settings' kind. */
function recordQuantity(record: StepRecord, step: Step, sources: readonly Source[]): "pressure" | "vacuum" | "flow" {
  const profile = (step.actions ?? []).find((action): action is RunSource => action.kind === "source_profile" && action.source === record.source);
  if (profile) return profile.quantity;
  const kinds = sources.find((source) => source.name === record.source)?.settings.map((setting) => setting.kind) ?? [];
  return kinds.includes("flow") && !kinds.includes("pressure") ? "flow" : kinds.includes("vacuum") && !kinds.includes("pressure") ? "vacuum" : "pressure";
}

/**
 * A check of a record against a spec (D54), to start from: at least the
 * wetted filter's rated bubble point where the preview has one, else at
 * least the level the record should read.
 */
export function specCheck(record: StepRecord, step: Step, preview: StepPreview | undefined, localId: string): Check {
  // A source's record: its own profile's bubble point; a sensor's peak: the step's profile's (D56).
  const rated =
    record.source || (record.measure !== undefined && record.measure !== "value")
      ? preview?.findings.find(
          (finding) => finding.code === "bubble_point" && finding.point !== undefined && (!record.source || finding.source === record.source),
        )
      : undefined;
  if (rated) {
    // The filter's own figure, as it stands: it follows the component (D55).
    return {
      local_id: localId,
      record: record.local_id,
      comparison: "ge",
      parameter: `${rated.refs[0]}.bubble_point`,
      description: "At least the filter's rated bubble point",
    };
  }
  const profile = (step.actions ?? []).find((action): action is RunSource => action.kind === "source_profile" && action.source === record.source);
  const value = (profile ? profileValues(profile).at(-1) : undefined) ?? 0;
  return {
    local_id: localId,
    record: record.local_id,
    // At least the spec: for a vacuum, at least as deep, so at or below it.
    comparison: value < 0 ? "le" : "ge",
    value,
    description: "",
  };
}

/** The findings of the last screening that the preview met in step `stepId`. */
function screenedIn(s: ScriptsState, stepId: string): ScreenedFinding[] {
  return (s.screening?.findings ?? []).filter((finding) => finding.steps?.includes(stepId));
}

function flagged(s: ScriptsState, stepId: string | undefined): boolean {
  return stepId !== undefined && screenedIn(s, stepId).length > 0;
}

function relativeTime(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  return new Date(iso).toLocaleString();
}

/** A flag: findings. For the canvas toolbar's findings toggle. */
function FindingsIcon() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 16V2.5M4 3h9l-2 3.25L13 9.5H4" />
    </svg>
  );
}

/** The canvas toolbar's button that shows or hides the findings window. */
export function FindingsToggle({ scripts: s }: { scripts: ScriptsState }) {
  const count = s.screening?.findings.length ?? 0;
  return (
    <button
      type="button"
      aria-label="Findings"
      aria-pressed={s.findingsOpen}
      title={`${s.findingsOpen ? "Hide" : "Show"} findings${s.screening ? ` (${count})` : ""}`}
      className={s.findingsOpen ? "ws-view-on" : undefined}
      onClick={() => s.setFindingsOpen(!s.findingsOpen)}
    >
      <FindingsIcon />
    </button>
  );
}

/**
 * Findings, in a window over the drawing. Nothing is found until the author
 * screens the script: a script being written fails most checks, and findings
 * nobody asked for are noise. What a screening found stays until the next,
 * marked out of date once anything it looked at changes.
 */
export function ScriptFindings({
  scripts: s,
  design,
  onShow,
}: {
  scripts: ScriptsState;
  design: DesignDocument;
  /** Highlight these on the drawing, and bring them into view. */
  onShow: (refs: string[]) => void;
}) {
  const steps = s.script?.content.steps ?? [];
  const screening = s.screening;
  const [chosen, setChosen] = useState<number | null>(null);
  useEffect(() => setChosen(null), [screening]);
  const present = useMemo(
    () => new Set([...(design.nodes ?? []), ...(design.edges ?? [])].map((element) => element.local_id)),
    [design],
  );
  // A port is drawn on its component.
  const shown = (finding: ScreenedFinding) => [
    ...new Set((finding.refs ?? []).map((ref) => ref.split(".")[0]!).filter((ref) => present.has(ref))),
  ];
  const where = (stepId: string) => {
    if (stepId === "") return { index: -1, label: "Step 0" };
    const index = steps.findIndex((step) => step.local_id === stepId);
    return { index, label: index >= 0 ? `Step ${index + 1}` : stepId };
  };
  let status: string;
  if (s.screeningBusy) status = "Screening…";
  else if (!screening) status = "Not screened yet";
  else status = `Screened ${relativeTime(screening.at)}${s.screeningStale ? " · changed since" : ""}`;
  return (
    <section className="sx-findings-window" aria-label="Findings">
      <div className="sx-findings-head">
        <h3>Findings</h3>
        <button type="button" className="primary" disabled={!s.script || s.screeningBusy} onClick={() => void s.screen()}>
          {screening ? "Screen again" : "Screen"}
        </button>
        <button type="button" className="sx-icon" aria-label="Close findings" title="Close" onClick={() => s.setFindingsOpen(false)}>
          ×
        </button>
      </div>
      <p className="ws-muted ws-small" role="status">
        {status}
      </p>
      {screening?.problem && <p className="sx-problem">{screening.problem}</p>}
      {screening &&
        (screening.findings.length === 0 ? (
          <p className="ws-muted ws-small">Nothing found.</p>
        ) : (
          <ul className="sx-findings">
            {screening.findings.map((finding, index) => {
              const refs = shown(finding);
              const first = finding.steps?.map(where).find((at) => at.index >= 0 || at.label === "Step 0");
              const go = () => {
                setChosen(index);
                if (first) s.selectStep(first.index);
                if (refs.length > 0) onShow(refs);
              };
              return (
              <li key={`${finding.code}-${index}`} data-severity={finding.severity} aria-current={chosen === index || undefined}>
                {refs.length > 0 || first ? (
                  <button
                    type="button"
                    className="sx-finding"
                    title={refs.length > 0 ? `Show ${refs.join(", ")} on the drawing` : `Go to ${first!.label}`}
                    onClick={go}
                  >
                    {finding.message}
                  </button>
                ) : (
                  finding.message
                )}
                {finding.steps && finding.steps.length > 1 && (
                  <span className="sx-finding-steps">
                    {finding.steps.map((stepId) => {
                      const at = where(stepId);
                      return (
                        <button
                          key={stepId || "zero"}
                          type="button"
                          className="sx-small-button"
                          disabled={stepId !== "" && at.index < 0}
                          onClick={() => {
                            setChosen(index);
                            s.selectStep(at.index);
                            if (refs.length > 0) onShow(refs);
                          }}
                        >
                          {at.label}
                        </button>
                      );
                    })}
                  </span>
                )}
              </li>
              );
            })}
          </ul>
        ))}
    </section>
  );
}

/** A bin: delete. Drawn, so it matches the other small controls in weight. */
function TrashIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
      <path d="M1.5 3h9M4.5 3V1.8h3V3M2.8 3l.6 7.4h5.2L9.2 3M5 5v3.6M7 5v3.6" />
    </svg>
  );
}

/**
 * Over the drawing's top left: which step the drawing shows, and back and
 * forward through them. What a badge or a port's status is set to on the
 * drawing goes into this step.
 */
export function StepNavigator({ scripts: s }: { scripts: ScriptsState; design?: DesignDocument }) {
  const id = useId();
  const steps = s.script?.content.steps ?? [];
  const at = s.stepIndex;
  return (
    <div className="sx-nav" role="group" aria-label="Step shown">
      <button type="button" aria-label="Previous step" title="Previous step" disabled={at <= -1} onClick={() => s.selectStep(at - 1)}>
        ◀
      </button>
      <label htmlFor={id} className="visually-hidden">
        Step
      </label>
      <select id={id} value={at} onChange={(event) => s.selectStep(Number(event.target.value))}>
        <option value={-1}>0 · {s.parent ? `After ${s.parent.name}` : "Initial conditions"}</option>
        {steps.map((candidate, index) => (
          <option key={candidate.local_id} value={index}>
            {index + 1} · {candidate.label}
          </option>
        ))}
      </select>
      <button type="button" aria-label="Next step" title="Next step" disabled={at >= steps.length - 1} onClick={() => s.selectStep(at + 1)}>
        ▶
      </button>
    </div>
  );
}

/** A pump's operation in the step, edited on the pump. */
function PumpCanvasEditor({ name, at, step, close, children }: { name: string; at: { x: number; y: number }; step: number; close: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useKeepInside(ref);
  return (
    <div
      ref={ref}
      data-label-editor=""
      className="cx-port-editor cx-pump-editor"
      role="dialog"
      aria-label={`${name} in step ${step + 1}`}
      style={{ left: at.x, top: at.y }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
      }}
    >
      {children}
      <button type="button" onClick={close}>
        Done
      </button>
    </div>
  );
}

/**
 * The in-place editor on the drawing for what the preview is editing: a
 * port's status (at step zero, where it starts; in a step, what the step
 * sets it to), or a bag's contents at step zero.
 */
export function ScriptsCanvasEditor({
  scripts: s,
  design,
  edit,
  at,
  close,
}: {
  scripts: ScriptsState;
  design: DesignDocument;
  edit: CanvasEdit;
  at: { x: number; y: number };
  close: () => void;
}) {
  const node = design.nodes?.find((candidate) => candidate.local_id === edit.ref);
  if (!node || !s.preview) return null;
  const name = node.label || node.local_id;
  if (edit.kind === "pump") {
    const step = s.stepIndex >= 0 ? s.script?.content.steps?.[s.stepIndex] : undefined;
    if (!step) return null;
    const operation = step.actions?.find((action): action is RunPump => action.kind === "pump" && action.target === edit.ref) ?? null;
    return (
      <PumpCanvasEditor name={name} at={at} step={s.stepIndex} close={close}>
        <PumpFields pump={node} operation={operation} onChange={(next) => s.setPump(edit.ref, next)} />
      </PumpCanvasEditor>
    );
  }
  if (edit.kind === "regulator") {
    const now = s.preview.regulators?.[edit.ref];
    if (now?.sweep) return null;
    return (
      <RegulatorSettingEditor
        key={`${edit.ref}-${s.stepIndex}`}
        name={name}
        initial={now?.asked ?? null}
        vacuum={now?.gas === "vacuum"}
        x={at.x}
        y={at.y}
        onCommit={(value) => {
          s.setRegulator(edit.ref, value);
          close();
        }}
        onCancel={close}
      />
    );
  }
  if (edit.kind === "port") {
    const now = s.preview.ports[edit.ref];
    return (
      <PortSettingEditor
        key={`${edit.ref}-${s.stepIndex}`}
        name={name}
        initial={{ kind: now?.kind ?? "sealed", magnitude: now?.magnitude ?? null }}
        x={at.x}
        y={at.y}
        onCommit={(value) => {
          s.setPortSetting(edit.ref, value.kind, value.magnitude);
          close();
        }}
        onCancel={close}
      />
    );
  }
  const conditions = s.conditions?.content;
  const fill = conditions?.initial_fill?.find((entry) => entry.target === edit.ref);
  const role = conditions?.designations?.find((entry) => entry.target === edit.ref)?.role ?? "";
  const collapsible = node.role === "reservoir" && !isRigid(node, s.templates);
  return (
    <BagEditor
      key={edit.ref}
      name={name}
      fluids={(conditions?.fluids ?? []).map((fluid) => ({ name: fluid.name, label: fluid.description || fluid.name }))}
      nextColour={fluidColourOf([...(conditions?.fluids ?? []), { name: "" }], (conditions?.fluids ?? []).length)}
      initial={{ fluid: fill?.fluid ?? AIR, volume: fill?.volume ?? null, headspace: fill?.headspace ?? null, role }}
      collapsible={collapsible}
      x={at.x}
      y={at.y}
      onCommit={(value) => {
        const fluid = value.newFluid ? s.addFluid(value.newFluid, value.newColour) : value.fluid;
        s.setFill(edit.ref, { fluid, volume: value.volume, headspace: value.headspace });
        s.setDesignation(edit.ref, value.role);
        close();
      }}
      onCancel={close}
    />
  );
}

// --- The right side -----------------------------------------------------------

export function ScriptsEditor({
  scripts: s,
  design,
  templates,
  onShow,
}: {
  scripts: ScriptsState;
  design: DesignDocument;
  templates: Readonly<Record<string, TemplateDocument>>;
  /** Select these on the drawing and bring them into view. */
  onShow?: (refs: string[]) => void;
}) {
  if (s.editing === "conditions" && s.conditions) {
    return <ConditionsEditor scripts={s} design={design} />;
  }
  const step = s.stepIndex >= 0 ? s.script?.content.steps?.[s.stepIndex] : undefined;
  // Keyed by step: each opens its sections by what it holds.
  if (step) return <StepEditor key={step.local_id} scripts={s} step={step} design={design} templates={templates} onShow={onShow} />;
  if (s.script) return <InitialConditions scripts={s} design={design} />;
  return (
    <div className="ws-inspector sx-editor">
      <h2 className="sx-title">{s.script ? "Start" : "Workflows"}</h2>
      <p className="ws-muted ws-small">
        {s.script
          ? "Before the first step: each component in its initial state, each source at its initial setting, and the initial fill."
          : "Create a script, and a condition set for it to run against."}
      </p>
      {s.conditions && (
        <button type="button" onClick={s.editConditionsPanel}>
          Edit condition set
        </button>
      )}
    </div>
  );
}

function StepEditor({
  scripts: s,
  step,
  design,
  templates,
  onShow,
}: {
  scripts: ScriptsState;
  step: Step;
  design: DesignDocument;
  templates: Readonly<Record<string, TemplateDocument>>;
  onShow?: (refs: string[]) => void;
}) {
  const index = s.stepIndex;
  const update = (change: (step: Step) => Step) =>
    s.editScript((script) => ({ ...script, steps: (script.steps ?? []).map((candidate, i) => (i === index ? change(candidate) : candidate)) }));
  const nodes = design.nodes ?? [];
  const mixers = nodes.filter((node) => node.role === "mixer");
  const zones = design.thermal_zones ?? [];
  const regulators = nodes.filter((node) => node.role === "regulator");
  const sensors = nodes.filter((node) => node.role === "sensor");
  // Where a delivered volume is counted: a flow sensor, else a pump, else a part liquid passes.
  const meterRank = (node: NodeDocument) =>
    node.role === "sensor" && observesOf(node, templates) === "flow_rate" ? 0 : node.role === "pump" ? 1 : ["valve", "aseptic_connector", "port", "filter"].includes(node.role) ? 2 : -1;
  const meters = nodes
    .filter((node) => meterRank(node) >= 0)
    .sort((a, b) => meterRank(a) - meterRank(b) || (a.label || a.local_id).localeCompare(b.label || b.local_id))
    .map((node) => ({ value: node.local_id, label: node.label || node.local_id }));
  const vesselOptions = nodes
    .filter((node) => node.role === "reservoir" || node.role === "chamber" || node.role === "variable_volume_chamber")
    .sort((a, b) => (a.label || a.local_id).localeCompare(b.label || b.local_id))
    .map((node) => ({ value: node.local_id, label: node.label || node.local_id }));
  const sources = s.conditions?.content.sources ?? [];
  // Sources that can supply a pressure, a vacuum or a flow: those can run a profile (D52).
  const profiled = sources
    .map((source) => ({
      source,
      quantities: (["pressure", "vacuum", "flow"] as const).filter((quantity) => source.settings.some((setting) => setting.kind === quantity)),
    }))
    .filter((entry) => entry.quantities.length > 0);
  const actions = step.actions ?? [];
  const setOperation = (key: string, action: Action | null) =>
    update((current) => {
      const rest = (current.actions ?? []).filter((other) => actionKey(other) !== key);
      return { ...current, actions: action ? [...rest, action] : rest };
    });
  const units = useLiquidUnits();
  const sensorUnit = (id: string) => readingUnit(observesOf(nodes.find((n) => n.local_id === id) ?? ({} as NodeDocument), templates), units);
  const allIds = (s.script?.content.steps ?? []).flatMap((candidate) => [
    candidate.local_id,
    ...(candidate.checks ?? []).map((c) => c.local_id),
    ...(candidate.records ?? []).map((r) => r.local_id),
  ]);
  const names = Object.fromEntries(nodes.map((node) => [node.local_id, node.label || node.local_id]));
  const checkOptions = [
    ...(step.records ?? []).map((record) => ({ value: `record:${record.local_id}`, label: `record ${record.description || record.local_id}` })),
    ...sensors.map((node) => ({ value: `sensor:${node.local_id}`, label: labelOf(node) })),
  ];
  // What a check's quantity is in SI, to offer the components' parameters of that unit as its spec (D55).
  const SI_OF: Record<string, string> = { pressure: "pascal", vacuum: "pascal", flow: "meter ** 3 / second", flow_rate: "meter ** 3 / second", temperature: "kelvin" };
  const sensorSi = (sensor: string) => SI_OF[observesOf(nodes.find((n) => n.local_id === sensor) ?? ({} as NodeDocument), templates) ?? ""] ?? null;
  const recordSi = (record: StepRecord) => (record.sensor ? sensorSi(record.sensor) : SI_OF[recordQuantity(record, step, sources)]!);
  const specValue = (reference: string) => {
    const [owner, key] = reference.split(".");
    const node = nodes.find((candidate) => candidate.local_id === owner);
    return node && key ? parameterValue(node, templates, key) : null;
  };
  const specOptions = (si: string | null, unitShown: { label: string; factor: number }) =>
    specChoices(nodes, templates, s.previews[index], si, unitShown);
  // A record reads in its sensor's unit, or its source's: kPa, or the design's flow unit.
  const recordUnit = (record: StepRecord) => {
    if (record.sensor) return sensorUnit(record.sensor);
    const quantity = recordQuantity(record, step, sources);
    return quantity === "flow" ? { label: units.flowRate.label, factor: units.flowRate.factor } : { label: "kPa", factor: 1000 };
  };
  const recordOptions = [
    ...sensors.map((node) => ({ value: `sensor:${node.local_id}`, label: labelOf(node) })),
    ...sources.map((source) => ({ value: `source:${source.name}`, label: `${source.description || source.name} (source)` })),
  ];
  const flaggedHere = screenedIn(s, step.local_id).length;
  const airSensors = new Set(sensors.filter((node) => observesOf(node, templates) === "air_in_line").map((node) => node.local_id));
  const records = step.records ?? [];
  const checks = step.checks ?? [];
  // Items just added start open; the rest are one line each.
  const [added, setAdded] = useState<string | null>(null);

  // --- What each section holds, folded -------------------------------------------
  const profilesHere = actions.filter((action): action is RunSource => action.kind === "source_profile");
  const setpoints = actions.filter((action): action is SetSetpoint => action.kind === "setpoint");
  const sweeps = actions.filter((action): action is RunSetpoint => action.kind === "setpoint_profile");
  // Held or swept, a regulator has one setpoint in a step (D65).
  const setHere = new Set([...setpoints, ...sweeps].map((action) => action.target));
  const topOf = (id: string) => {
    const node = nodes.find((candidate) => candidate.local_id === id);
    const top = node ? parameterValue(node, templates, "set_pressure_max") : null;
    return typeof top === "number" ? top : null;
  };
  const sourceName = (name: string) => sources.find((source) => source.name === name)?.description || name;
  const profileSummary = [
    ...profilesHere.map((profile) => (
      <span key={`profile-${profile.source}`} className="sx-glyphs" title={`${sourceName(profile.source)} ${profileText(profile, units)}`}>
        <ProfileGlyph profile={profile} />
        {`${sourceName(profile.source)} ${profileShort(profile, units)}`}
      </span>
    )),
    ...sweeps.map((sweep) => (
      <span key={`sweep-${sweep.target}`} className="sx-glyphs" title={`${names[sweep.target] ?? sweep.target} ${profileText(sweep, units)}`}>
        <ProfileGlyph profile={sweep} />
        {`${names[sweep.target] ?? sweep.target} ${profileShort(sweep, units)}`}
      </span>
    )),
    ...setpoints.map((setpoint) => (
      <span key={`setpoint-${setpoint.target}`}>{`${names[setpoint.target] ?? setpoint.target} ${setpointText(setpoint)}`}</span>
    )),
  ].flatMap((part, i) => (i ? [<span key={`dot-${i}`}> · </span>, part] : [part]));
  // The time again only where something else could end the step: else it is the finish itself.
  const endTime = stepTime(step);
  const endSummary = (
    <>
      <FinishGlyphs step={step} names={names} sensorUnit={sensorUnit} airSensors={airSensors} units={units} />
      {(step.finish?.conditions ?? []).length > 0 || step.finish?.complete ? ` · ${stepTimeText(endTime)}` : null}
    </>
  );
  const recordName = (record: StepRecord) =>
    record.sensor ? (names[record.sensor] ?? record.sensor) : sourceName(record.source ?? "");
  const recordText = (record: StepRecord) =>
    `${record.description || recordName(record)}${record.measure && record.measure !== "value" ? ` ${record.measure}` : ""}${record.when === "met" ? " at end point" : ""}`;
  const checkText = (check: Check) => {
    const record = check.record ? records.find((candidate) => candidate.local_id === check.record) : undefined;
    const of = record ? recordText(record) : `${names[check.sensor ?? ""] ?? check.sensor}${check.measure === "change" ? " change" : ""}`;
    const unit = record ? recordUnit(record) : sensorUnit(check.sensor ?? "");
    const spec = check.parameter
      ? `${names[check.parameter.split(".")[0]!] ?? check.parameter.split(".")[0]} ${check.parameter.split(".")[1]!.replace(/_/g, " ")}`
      : `${Number(((check.value ?? 0) / unit.factor).toPrecision(4))}${unit.label && !unit.label.startsWith("(") ? ` ${unit.label}` : ""}`;
    return `${of} ${({ lt: "<", le: "≤", gt: ">", ge: "≥" } as Record<string, string>)[check.comparison]} ${spec}`;
  };
  const signalSummary = [...records.map(recordText), ...checks.map((check) => `✓ ${checkText(check)}`)].join(" · ");

  // --- Adding --------------------------------------------------------------------
  const addAction = (value: string) => {
    const [kind, id] = value.split(/:(.*)/) as [string, string];
    if (kind === "profile") {
      const entry = profiled.find((candidate) => candidate.source.name === id)!;
      setOperation(`source:${id}`, sourceProfile(entry.source, entry.quantities[0]!, "stairs"));
      setAdded(`profile:${id}`);
    } else if (kind === "sweep") {
      setOperation(`setpoint:${id}`, regulatorProfile(id, "stairs", topOf(id)));
      setAdded(`sweep:${id}`);
    } else if (kind === "speed" || kind === "temperature" || kind === "pressure") {
      // 60 rpm, 37 °C or 100 kPa: a start the author edits.
      const value = kind === "temperature" ? 310.15 : kind === "pressure" ? 100_000 : 1;
      setOperation(`setpoint:${id}`, { kind: "setpoint", target: id, quantity: kind, value });
    }
  };
  const profileOptions = [
    ...profiled
      .filter(({ source }) => !profilesHere.some((profile) => profile.source === source.name))
      .map(({ source }) => ({ value: `profile:${source.name}`, label: source.description || source.name, group: "Ramp or staircase" })),
    ...regulators.filter((regulator) => !setHere.has(regulator.local_id)).map((regulator) => ({ value: `sweep:${regulator.local_id}`, label: labelOf(regulator), group: "Ramp or staircase" })),
    ...mixers.filter((mixer) => !setpoints.some((point) => point.target === mixer.local_id)).map((mixer) => ({ value: `speed:${mixer.local_id}`, label: labelOf(mixer), group: "Setpoint" })),
    ...zones.filter((zone) => !setpoints.some((point) => point.target === zone.local_id)).map((zone) => ({ value: `temperature:${zone.local_id}`, label: zone.local_id, group: "Setpoint" })),
    ...regulators.filter((regulator) => !setHere.has(regulator.local_id)).map((regulator) => ({ value: `pressure:${regulator.local_id}`, label: labelOf(regulator), group: "Setpoint" })),
  ];
  const addSignal = (value: string) => {
    const local_id = nextId(value === "check" ? "check" : "record", allIds);
    if (value === "check") {
      update((current) => ({
        ...current,
        checks: [...(current.checks ?? []), { local_id, sensor: sensors[0]?.local_id ?? null, measure: "change", comparison: "gt", value: -2000 }],
      }));
      setAdded(local_id);
      return;
    }
    // With a profile: the gauge nearest its source, at its highest (lowest, for a vacuum) over the step (D56);
    // else the first sensor, as the step ends.
    const profile = profilesHere[0];
    const port = profile ? sources.find((source) => source.name === profile.source)?.port : undefined;
    const gauge = profile && port && profile.quantity !== "flow" ? nearestSensor(design, templates, port, "pressure") : null;
    const record: StepRecord = gauge
      ? { local_id, sensor: gauge, measure: profile!.quantity === "vacuum" ? "min" : "max", when: "end" }
      : profile
        ? { local_id, source: profile.source, when: "met" }
        : { local_id, sensor: sensors[0]?.local_id ?? null, source: sensors[0] ? null : (sources[0]?.name ?? null), when: "end" };
    update((current) => ({ ...current, records: [...(current.records ?? []), record] }));
    setAdded(local_id);
  };
  const removeRecord = (id: string) =>
    // Its checks go with it: a check on a record the step no longer makes is refused.
    update((current) => ({
      ...current,
      records: (current.records ?? []).filter((record) => record.local_id !== id),
      checks: (current.checks ?? []).filter((check) => check.record !== id),
    }));

  return (
    <div className="ws-inspector sx-editor sx-dense" aria-label={`Step ${index + 1}`}>
      <SheetTable label="Step">
        <SheetRows>
          <TextField label={`Step ${index + 1}`} value={step.label} onChange={(label) => update((current) => ({ ...current, label: label || current.label }))} />
        </SheetRows>
      </SheetTable>
      {flaggedHere > 0 && <p className="sx-problem">Screening flagged {flaggedHere} here: see Findings.</p>}

      {/* Valves, pumps and ports are set in the table and on the drawing; here only what they cannot: profiles and setpoints. */}
      {profileOptions.length + profilesHere.length + sweeps.length + setpoints.length > 0 && (
        <Fold id="step-profiles" title="Profiles" summary={profileSummary.length ? profileSummary : "none"} empty={profilesHere.length + sweeps.length + setpoints.length === 0}>
          <SheetTable label="Profiles">
            {profilesHere.map((profile) => {
              const entry = profiled.find((candidate) => candidate.source.name === profile.source);
              if (!entry) return null;
              return (
                <FoldRow
                  key={`profile-${profile.source}`}
                  label={`${sourceName(profile.source)} profile`}
                  summary={
                    <span className="sx-glyphs">
                      <ProfileGlyph profile={profile} />
                      {`${sourceName(profile.source)} ${profileText(profile, units)}`}
                    </span>
                  }
                  defaultOpen={added === `profile:${profile.source}`}
                  onRemove={() => setOperation(`source:${profile.source}`, null)}
                >
                  <SourceProfileFields bare source={entry.source} quantities={entry.quantities} profile={profile} onChange={(next) => setOperation(`source:${profile.source}`, next)} />
                </FoldRow>
              );
            })}
            {sweeps.map((sweep) => (
              <FoldRow
                key={`sweep-${sweep.target}`}
                label={`${names[sweep.target] ?? sweep.target} profile`}
                summary={
                  <span className="sx-glyphs">
                    <ProfileGlyph profile={sweep} />
                    {`${names[sweep.target] ?? sweep.target} ${profileText(sweep, units)}`}
                  </span>
                }
                defaultOpen={added === `sweep:${sweep.target}`}
                onRemove={() => setOperation(`setpoint:${sweep.target}`, null)}
              >
                <RegulatorProfileFields
                  target={sweep.target}
                  name={names[sweep.target] ?? sweep.target}
                  top={topOf(sweep.target)}
                  profile={sweep}
                  onChange={(next) => setOperation(`setpoint:${sweep.target}`, next)}
                />
              </FoldRow>
            ))}
            {setpoints.length > 0 && (
              <SheetRows label="Setpoints">
                {setpoints.map((setpoint) => (
                  <SetpointFields
                    key={`setpoint-${setpoint.target}`}
                    target={setpoint.target}
                    name={names[setpoint.target] ?? setpoint.target}
                    quantity={setpoint.quantity}
                    setpoint={setpoint}
                    onChange={(next) => setOperation(`setpoint:${setpoint.target}`, next)}
                  />
                ))}
              </SheetRows>
            )}
          </SheetTable>
          <AddSelect label="Add" options={profileOptions} onAdd={addAction} />
        </Fold>
      )}

      <Fold id="step-endpoint" title="Endpoint" summary={endSummary}>
        <EndpointFields
          step={step}
          sensors={sensors}
          sensorUnit={sensorUnit}
          airSensors={airSensors}
          names={names}
          meters={meters}
          vessels={vesselOptions}
          onChange={(finish) => update((current) => ({ ...current, finish }))}
        />
      </Fold>

      <Fold id="step-signals" title="Signals" summary={signalSummary || "none"} empty={records.length + checks.length === 0}>
        <SheetTable label="Signals">
          {records.map((record, at) => {
            const setRecord = (next: StepRecord) =>
              update((current) => ({ ...current, records: (current.records ?? []).map((r, i) => (i === at ? next : r)) }));
            const what = record.source ? `source:${record.source}` : `sensor:${record.sensor ?? ""}`;
            const expected = expectedRecord(record, step, s.previews[index], units, names);
            return (
              <FoldRow
                key={record.local_id}
                label={`Record ${at + 1}`}
                summary={recordText(record)}
                defaultOpen={added === record.local_id}
                onRemove={() => removeRecord(record.local_id)}
              >
                <Select
                  label="Record"
                  value={what}
                  options={recordOptions}
                  onChange={(next) => {
                    const [kind, id] = next.split(/:(.*)/) as [string, string];
                    setRecord({ ...record, sensor: kind === "sensor" ? id : null, source: kind === "source" ? id : null });
                  }}
                />
                <Select
                  label="Measure"
                  value={record.measure ?? "value"}
                  options={[
                    { value: "value", label: "value" },
                    { value: "max", label: "max" },
                    { value: "min", label: "min" },
                    ...(record.measure === "peak" ? [{ value: "peak" as const, label: "peak" }] : []),
                  ]}
                  onChange={(measure) => setRecord({ ...record, measure })}
                />
                <Select
                  label={record.measure && record.measure !== "value" ? "Up to" : "When"}
                  value={record.when ?? "end"}
                  options={[
                    { value: "end", label: "step end" },
                    { value: "met", label: "end point met" },
                  ]}
                  onChange={(when) => setRecord({ ...record, when })}
                />
                <TextField label="Name" value={record.description ?? ""} onChange={(description) => setRecord({ ...record, description })} />
                {expected && (
                  <FieldRow label="Expect" span>
                    <span className="ws-muted">{expected}</span>
                  </FieldRow>
                )}
                <FieldRow label="" span>
                  <button
                    type="button"
                    className="sx-small-button"
                    onClick={() => {
                      const id = nextId("check", allIds);
                      update((current) => ({ ...current, checks: [...(current.checks ?? []), specCheck(record, step, s.previews[index], id)] }));
                      setAdded(id);
                    }}
                  >
                    Check against a spec
                  </button>
                </FieldRow>
              </FoldRow>
            );
          })}
          {checks.map((check, at) => {
            const setCheck = (next: Check) =>
              update((current) => ({ ...current, checks: (current.checks ?? []).map((c, i) => (i === at ? next : c)) }));
            const record = check.record ? records.find((candidate) => candidate.local_id === check.record) : undefined;
            const unit = record ? recordUnit(record) : sensorUnit(check.sensor ?? "");
            const options = specOptions(record ? recordSi(record) : sensorSi(check.sensor ?? ""), unit);
            const missing = check.parameter && !options.some((option) => option.value === check.parameter);
            const resolved = check.parameter ? specValue(check.parameter) : null;
            const owner = check.parameter?.split(".")[0];
            return (
              <FoldRow
                key={check.local_id}
                label={`Check ${at + 1}`}
                summary={`✓ ${checkText(check)}`}
                defaultOpen={added === check.local_id}
                onRemove={() => update((current) => ({ ...current, checks: (current.checks ?? []).filter((_, i) => i !== at) }))}
              >
                <Select
                  label="Of"
                  value={check.record ? `record:${check.record}` : `sensor:${check.sensor ?? ""}`}
                  options={checkOptions}
                  onChange={(next) => {
                    const [kind, id] = next.split(/:(.*)/) as [string, string];
                    setCheck(kind === "record" ? { ...check, record: id, sensor: null, measure: "reading" } : { ...check, sensor: id, record: null });
                  }}
                />
                {!check.record && (
                  <Select
                    label="Measure"
                    value={check.measure ?? "reading"}
                    options={[
                      { value: "reading", label: "at end" },
                      { value: "change", label: "change in step" },
                    ]}
                    onChange={(measure) => setCheck({ ...check, measure })}
                  />
                )}
                <Select label="Must be" value={check.comparison} options={COMPARISONS} onChange={(comparison: Comparison) => setCheck({ ...check, comparison })} />
                {(options.length > 0 || check.parameter) && (
                  <Select
                    label="Against"
                    value={check.parameter ?? ""}
                    options={[
                      { value: "", label: "a value" },
                      ...options,
                      ...(missing ? [{ value: check.parameter!, label: `${check.parameter} (not in the design)` }] : []),
                    ]}
                    onChange={(parameter) =>
                      setCheck(parameter ? { ...check, parameter, value: null } : { ...check, parameter: null, value: specValue(check.parameter ?? "") ?? check.value ?? 0 })
                    }
                  />
                )}
                {check.parameter ? (
                  <FieldRow label="Which is" span>
                    <span className={resolved === null ? "sx-problem" : "ws-muted"}>
                      {resolved === null ? "not a number the design has" : `${Number((resolved / unit.factor).toPrecision(4))} ${unit.label} now`}
                    </span>{" "}
                    {onShow && owner && nodes.some((node) => node.local_id === owner) && (
                      <button type="button" className="sx-small-button" aria-label={`Show ${owner} on the drawing`} onClick={() => onShow([owner])}>
                        Show
                      </button>
                    )}
                  </FieldRow>
                ) : (
                  <NumberField label="Value" unit={unit.label} factor={unit.factor} value={check.value ?? null} onChange={(value) => value !== null && setCheck({ ...check, value })} />
                )}
                <TextField label="Name" value={check.description ?? ""} onChange={(description) => setCheck({ ...check, description })} />
              </FoldRow>
            );
          })}
        </SheetTable>
        <AddSelect
          label="Signal"
          options={[
            ...(recordOptions.length ? [{ value: "record", label: "record a value, max or min" }] : []),
            ...(checkOptions.length ? [{ value: "check", label: "check a sensor (pass or fail)" }] : []),
          ]}
          onAdd={addSignal}
        />
      </Fold>

      <Fold id="step-notes" title="Notes" summary={(step.notes ?? "").split("\n")[0] || "none"} empty={!step.notes}>
        <SheetTable label="Notes">
          <SheetRows>
            <FieldRow label="" span>
              <TextField label="Notes" area value={step.notes ?? ""} onChange={(notes) => update((c) => ({ ...c, notes }))} />
            </FieldRow>
          </SheetRows>
        </SheetTable>
      </Fold>

      <button type="button" className="sx-danger sx-small-button sx-delete-step" onClick={() => deleteStep(s, index)}>
        Delete step
      </button>
    </div>
  );
}

function ConditionsEditor({ scripts: s, design }: { scripts: ScriptsState; design: DesignDocument }) {
  const doc = s.conditions!.content;
  const sources = doc.sources ?? [];
  const fluids = doc.fluids ?? [];
  const fill = doc.initial_fill ?? [];
  const nodes = design.nodes ?? [];
  // Where a source can sit: external ports, and a drawn supply's own (D66).
  const externalPorts = nodes.flatMap((node) =>
    sourcePorts(node).map((port) => ({ value: port, label: `${node.label || node.local_id} · ${port.split(".")[1]}` })),
  );
  const portLabel = (port: string) => externalPorts.find((option) => option.value === port)?.label ?? port;
  const targets = [
    ...nodes.map((node) => ({ value: node.local_id, label: labelOf(node) })),
    ...(design.edges ?? []).map((edge) => ({ value: edge.local_id, label: edge.local_id })),
  ];
  const targetLabel = (target: string) => nodes.find((node) => node.local_id === target)?.label || target;
  // What a bag holds is set from the table, on the drawing; here only what lines and other components hold.
  const bagIds = new Set(nodes.filter((node) => node.role === "reservoir").map((node) => node.local_id));
  const others = fill.map((entry, at) => ({ entry, at })).filter(({ entry }) => !bagIds.has(entry.target));
  const otherTargets = targets.filter((option) => !bagIds.has(option.value));
  const fluidOptions = [{ value: "air", label: "air" }, ...fluids.map((fluid) => ({ value: fluid.name, label: fluid.name }))];
  const edit = s.editConditions;
  const setSource = (at: number, next: Source) => edit((c) => ({ ...c, sources: sources.map((x, i) => (i === at ? next : x)) }));
  const [added, setAdded] = useState<string | null>(null);
  const used = new Set(sources.map((source) => source.port));
  const sourceLine = (source: Source) => `${source.name} · ${portLabel(source.port)} · ${source.settings.map((setting) => setting.name).join(", ")}`;

  return (
    <div className="ws-inspector sx-editor sx-dense" aria-label="Condition set">
      <SheetTable label="Condition set">
        <SheetRows>
          <FieldRow label="Step 0">
            <button type="button" className="sx-link sx-cell-text" onClick={() => s.selectStep(-1)}>
              ← Initial conditions
            </button>
          </FieldRow>
          <FieldRow label="Set-up">
            <span className="sx-cell-text">{s.parent ? `shared with ${s.parent.name}` : "sources, settings, fluids"}</span>
          </FieldRow>
        </SheetRows>
      </SheetTable>

      <Fold id="conditions-sources" title="Sources" summary={sources.map(sourceLine).join(" · ") || "none"} empty={sources.length === 0}>
        <SheetTable label="Sources">
          {sources.map((source, at) => (
            <FoldRow
              key={at}
              label={`Source ${source.name}`}
              summary={sourceLine(source)}
              defaultOpen={added === source.name}
              onRemove={() => edit((c) => ({ ...c, sources: sources.filter((_, i) => i !== at) }))}
            >
              <TextField label="Name" value={source.name} onChange={(name) => setSource(at, { ...source, name: toLocalId(name, "source") })} />
              <Select label="On port" value={source.port} options={externalPorts} onChange={(port) => setSource(at, { ...source, port })} />
              {source.settings.map((setting, k) => {
                const setSetting = (next: typeof setting) => setSource(at, { ...source, settings: source.settings.map((x, i) => (i === k ? next : x)) });
                const magnitude = setting.kind === "pressure" || setting.kind === "vacuum";
                return (
                  <Fragment key={k}>
                    <FieldRow
                      label={<BareTextField label="Setting" value={setting.name} onChange={(name) => setSetting({ ...setting, name: toLocalId(name, "setting") })} />}
                      unit={
                        <button
                          type="button"
                          className="sx-icon"
                          aria-label={`Remove setting ${setting.name}`}
                          disabled={source.settings.length === 1}
                          onClick={() => {
                            const settings = source.settings.filter((_, i) => i !== k);
                            setSource(at, { ...source, settings, initial: settings.some((x) => x.name === source.initial) ? source.initial : settings[0]!.name });
                          }}
                        >
                          ×
                        </button>
                      }
                    >
                      <BareSelect
                        label="Kind"
                        value={setting.kind}
                        options={SOURCE_KINDS.map((kind) => ({ value: kind, label: kind }))}
                        onChange={(kind: SourceKind) => setSetting({ ...setting, kind, magnitude: null })}
                      />
                    </FieldRow>
                    {magnitude && (
                      <NumberField label="Gauge" unit="kPa" factor={1000} value={setting.magnitude ?? null} onChange={(next) => setSetting({ ...setting, magnitude: next })} />
                    )}
                  </Fragment>
                );
              })}
              <FieldRow label="" span>
                <button
                  type="button"
                  className="sx-small-button"
                  onClick={() => setSource(at, { ...source, settings: [...source.settings, { name: nextId("setting", source.settings.map((x) => x.name)), kind: "sealed" }] })}
                >
                  + setting
                </button>
              </FieldRow>
              <Select
                label="Starts"
                value={source.initial}
                options={source.settings.map((setting) => ({ value: setting.name, label: setting.name }))}
                onChange={(initial) => setSource(at, { ...source, initial })}
              />
            </FoldRow>
          ))}
        </SheetTable>
        <AddSelect
          label="Source on"
          options={externalPorts.filter((option) => !used.has(option.value))}
          onAdd={(port) => {
            const name = nextId("source", sources.map((x) => x.name));
            setAdded(name);
            edit((c) => ({
              ...c,
              sources: [
                ...sources,
                {
                  name,
                  port,
                  settings: [
                    { name: "off", kind: "sealed" },
                    { name: "on", kind: "pressure" },
                  ],
                  initial: "off",
                },
              ],
            }));
          }}
        />
      </Fold>

      <Fold id="conditions-fluids" title="Fluids" summary={fluids.map((fluid) => fluid.description || fluid.name).join(" · ") || "none"} empty={fluids.length === 0}>
        <SheetTable label="Fluids">
          <SheetRows>
            {fluids.map((fluid, at) => (
              <FieldRow
                key={at}
                label={
                  <BareTextField
                    label="Name"
                    value={fluid.name}
                    onChange={(name) => {
                      const next = toLocalId(name, "fluid");
                      edit((c) => ({
                        ...c,
                        fluids: fluids.map((x, i) => (i === at ? { ...x, name: next } : x)),
                        initial_fill: fill.map((f) => (f.fluid === fluid.name ? { ...f, fluid: next } : f)),
                      }));
                    }}
                  />
                }
                unit={
                  <button
                    type="button"
                    className="sx-icon"
                    aria-label={`Remove ${fluid.name}`}
                    onClick={() => edit((c) => ({ ...c, fluids: fluids.filter((_, i) => i !== at), initial_fill: fill.filter((f) => f.fluid !== fluid.name) }))}
                  >
                    ×
                  </button>
                }
              >
                <span className="sx-fluid">
                  {/* Its colour on the drawing: its own, else the one its place gives it. */}
                  <input
                    type="color"
                    className="sx-swatch"
                    aria-label={`Colour of ${fluid.description || fluid.name}`}
                    title="Colour on the drawing"
                    value={fluidColourOf(fluids, at)}
                    onChange={(event) => {
                      const colour = event.target.value;
                      edit((c) => ({ ...c, fluids: (c.fluids ?? []).map((x, i) => (i === at ? { ...x, colour } : x)) }));
                    }}
                  />
                  <BareTextField
                    label="Description"
                    placeholder="Description"
                    value={fluid.description ?? ""}
                    onChange={(description) => edit((c) => ({ ...c, fluids: fluids.map((x, i) => (i === at ? { ...x, description } : x)) }))}
                  />
                </span>
              </FieldRow>
            ))}
          </SheetRows>
        </SheetTable>
        <div className="sx-add">
          <button type="button" onClick={() => edit((c) => ({ ...c, fluids: [...fluids, { name: nextId("fluid", fluids.map((x) => x.name)) }] }))}>
            + fluid…
          </button>
        </div>
      </Fold>

      <Fold
        id="conditions-fill"
        title="Lines and parts at the start"
        summary={others.map(({ entry }) => `${targetLabel(entry.target)} ${entry.fluid}`).join(" · ") || "all air"}
        empty={others.length === 0}
      >
        <SheetTable label="At the start">
          <SheetRows>
            {others.map(({ entry, at }) => (
              <FieldRow
                key={at}
                label={<BareSelect label="Where" value={entry.target} options={otherTargets} onChange={(target) => edit((c) => ({ ...c, initial_fill: fill.map((x, i) => (i === at ? { ...x, target } : x)) }))} />}
                unit={
                  <button type="button" className="sx-icon" aria-label="Remove fill" onClick={() => edit((c) => ({ ...c, initial_fill: fill.filter((_, i) => i !== at) }))}>
                    ×
                  </button>
                }
              >
                <BareSelect label="Holds" value={entry.fluid} options={fluidOptions} onChange={(fluid) => edit((c) => ({ ...c, initial_fill: fill.map((x, i) => (i === at ? { ...x, fluid } : x)) }))} />
              </FieldRow>
            ))}
          </SheetRows>
        </SheetTable>
        {fluids.length > 0 && (
          <div className="sx-add">
            <button
              type="button"
              onClick={() => {
                const taken = new Set(fill.map((f) => f.target));
                const target = otherTargets.find((option) => !taken.has(option.value))?.value ?? otherTargets[0]?.value ?? "";
                edit((c) => ({ ...c, initial_fill: [...fill, { target, fluid: fluids[0]!.name }] }));
              }}
            >
              + fill…
            </button>
          </div>
        )}
      </Fold>
    </div>
  );
}


// --- Step zero ----------------------------------------------------------------

const DESIGNATIONS = [
  { value: "none", label: "—" },
  { value: "input", label: "input" },
  { value: "output", label: "output" },
] as const;

/**
 * Step zero (D43, D44): where everything starts. What bags hold and ports'
 * status are the condition set's, which the script names and the author
 * never has to choose; where each valve, pump and connector starts is the
 * script's (invariant 8). After a parent, everything starts as the parent
 * leaves it, and only states and port settings can be changed here.
 */
function InitialConditions({ scripts: s, design }: { scripts: ScriptsState; design: DesignDocument }) {
  const nodes = design.nodes ?? [];
  const script = s.script!.content;
  const conditions = s.conditions?.content;
  const start = s.columns[0];
  const parent = s.parent;
  // Where each valve, pump and connector starts, each port's status and what each bag holds are
  // set in the table and on the drawing; here only what they cannot: where the script starts from,
  // and which ports are where fluid comes in or goes out.
  const sourced = new Set((conditions?.sources ?? []).map((source) => source.port.split(".")[0]));
  const ports = nodes.filter(
    (node) => (node.role === "port" || sourced.has(node.local_id)) && !(start && !start.ports[node.local_id] && start.inlinePorts?.includes(node.local_id)),
  );
  const designation = (target: string) => conditions?.designations?.find((entry) => entry.target === target)?.role ?? "none";
  const parents = s.scripts.filter((candidate) => candidate.id !== s.script!.id);
  const nameOf = (node: NodeDocument) => node.label || node.local_id;
  const roles = ports.filter((node) => designation(node.local_id) !== "none").map((node) => `${nameOf(node)} ${designation(node.local_id)}`);

  return (
    <div className="ws-inspector sx-editor sx-dense" aria-label="Initial conditions">
      <SheetTable label="Step 0">
        <SheetRows>
          <FieldRow label="Step 0">
            <span className="sx-cell-text">{parent ? `After ${parent.name}` : "Initial conditions"}</span>
          </FieldRow>
          <Select
            label="Starts after"
            value={script.starts_after ?? "none"}
            options={[{ value: "none", label: "nothing" }, ...parents.map((candidate) => ({ value: candidate.id, label: candidate.name }))]}
            onChange={(value) => s.setStartsAfter(value === "none" ? null : value)}
          />
        </SheetRows>
      </SheetTable>

      {conditions && !parent && ports.length > 0 && (
        <Fold id="start-port-roles" title="Port roles" summary={roles.join(" · ") || "none"} empty={roles.length === 0}>
          <SheetTable label="Port roles">
            <SheetRows>
              {ports.map((node) => (
                <Select
                  key={node.local_id}
                  label={nameOf(node)}
                  value={designation(node.local_id)}
                  options={DESIGNATIONS}
                  onChange={(next) => s.setDesignation(node.local_id, next === "none" ? "" : next)}
                />
              ))}
            </SheetRows>
          </SheetTable>
        </Fold>
      )}

      {s.conditions && (
        <button type="button" className="sx-small-button sx-delete-step" onClick={s.editConditionsPanel}>
          Sources, settings and fluids…
        </button>
      )}
    </div>
  );
}


// --- The canvas menu, on the Scripts step -----------------------------------------

/**
 * What right-clicking the drawing offers while a script is open: set a
 * component or a source where the author is (the chosen step, or where it
 * starts), and the view. Nothing that edits the design: that is the
 * Schematic step's.
 */
export function scriptMenu(
  s: ScriptsState,
  design: DesignDocument,
  /** Open the right-hand panel, where step zero and each step are edited. */
  showEditor: () => void = () => undefined,
) {
  return (target: Target, context: CommandContext): MenuEntry[] => {
    const where = s.stepIndex < 0 ? "at the start" : `in step ${s.stepIndex + 1}`;
    const preview = s.preview;
    const view: MenuEntry[] = [
      { label: "Zoom to fit", shortcut: "⇧1", onSelect: () => context.view({ kind: "fit" }) },
      { label: "Zoom to 100%", shortcut: "0", onSelect: () => context.view({ kind: "zoom-reset" }) },
      {
        label: context.state.annotations.labels ? "Hide labels" : "Show labels",
        onSelect: () => context.dispatch({ type: "SET_ANNOTATIONS", labels: !context.state.annotations.labels }),
      },
    ];
    const steps: MenuEntry[] = [
      {
        label: "Edit initial conditions",
        onSelect: () => {
          s.selectStep(-1);
          showEditor();
        },
      },
    ];
    if (!s.script) return [{ label: "Open or create a script first", disabledReason: "No script is open" }, "separator", ...view];

    const ref = target.kind === "port" ? target.ref?.split(".")[0] : target.ref;
    const node = ref ? design.nodes?.find((candidate) => candidate.local_id === ref) : undefined;
    const entries: MenuEntry[] = [];
    // A bag's sealed or released is not what a preview turns on: what it holds is.
    if (node?.role === "reservoir") {
      entries.push(
        s.parent
          ? { label: `What ${node.label || node.local_id} holds comes from ${s.parent.name}`, disabledReason: "Set in the script it starts after" }
          : {
              label: `Contents of ${node.label || node.local_id} at the start…`,
              onSelect: () => {
                s.selectStep(-1);
                s.setCanvasEdit({ kind: "bag", ref: node.local_id });
              },
            },
      );
    } else if (node?.role === "pump" && s.stepIndex >= 0) {
      // In a step, a pump is given an operation, not just on or off (D45).
      entries.push({
        label: `What ${node.label || node.local_id} does ${where}…`,
        onSelect: () => s.setCanvasEdit({ kind: "pump", ref: node.local_id }),
      });
    } else if (node?.state_set?.length) {
      const now = preview?.states[node.local_id];
      entries.push({
        label: `${node.label || node.local_id} ${where}`,
        submenu: node.state_set.map((state) => ({
          label: state === now ? `${state} ✓` : state,
          onSelect: () => s.setHere({ kind: "set_state", target: node.local_id, state }),
        })),
      });
    }
    const sources = (s.conditions?.content.sources ?? []).filter(
      (source) => node && source.port.split(".")[0] === node.local_id,
    );
    for (const source of sources) {
      const now = preview?.sources[source.name];
      entries.push({
        label: `${source.name} ${where}`,
        submenu: source.settings.map((setting) => ({
          label: `${setting.name} (${setting.kind})${setting.name === now ? " ✓" : ""}`,
          onSelect: () => s.setHere({ kind: "set_source", source: source.name, setting: setting.name }),
        })),
      });
    }
    const inline = node && s.preview?.inlinePorts?.includes(node.local_id) && sources.length === 0;
    if (inline) {
      entries.push({ label: `${node!.label || node!.local_id} is open: a connection between two lines`, disabledReason: "Nothing to set" });
    } else if (node && (node.role === "port" || sources.length > 0) && s.conditions) {
      entries.push({ label: `Status of ${node.label || node.local_id} ${where}…`, onSelect: () => s.setCanvasEdit({ kind: "port", ref: node.local_id }) });
    }
    if (node && entries.length === 0) {
      entries.push({ label: `${node.label || node.local_id} has nothing a script sets`, disabledReason: "No states or source" });
    }
    return [...entries, ...(entries.length ? ["separator" as const] : []), ...steps, "separator", ...view];
  };
}
